import { useEffect, useRef, useState } from "react";
import { useMileage } from "@/context/MileageContext";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import {
  UserCheck, ShieldCheck, CreditCard, Car, Check, X, Snowflake, Flame,
  Plus, Trash2, Building2, ArrowUpRight, AlertCircle, Lock, Zap, Users, DollarSign,
  HeartHandshake, Star, TrendingUp, Wifi, Radio, Download,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "approvals", label: "Approval Queue", icon: UserCheck },
  { key: "policy", label: "Policy Compliance", icon: ShieldCheck },
  { key: "cards", label: "Virtual Cards", icon: CreditCard },
  { key: "mileage", label: "Mileage Tracker", icon: Car },
  { key: "signals", label: "Advance Signals", icon: Zap },
  { key: "cpa_leads", label: "CPA Leads", icon: HeartHandshake },
  { key: "team", label: "Team Directory", icon: Users },
];

// ---------------- Approvals ----------------
const DECLINE_REASONS = [
  "Over policy cap for category",
  "Missing receipt documentation",
  "Duplicate submission detected",
  "Non-business expense",
  "Requires VP counter-signature",
  "Exceeds single-transaction limit",
];

function ApprovalsTab() {
  const [rows, setRows] = useState([]);
  const [achTotal, setAchTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fadingIds, setFadingIds] = useState(new Set());
  const [declineOpen, setDeclineOpen] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [hoverRowId, setHoverRowId] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [settling, setSettling] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [appRes, histRes] = await Promise.all([
        api.get("/v1/approvals").catch(() => ({ data: { rows: [], ach_intransit_total: 0 } })),
        api.get("/v1/approvals/history").catch(() => ({ data: [] })),
      ]);
      const appData = appRes.data || {};
      setRows(appData.rows || []);
      setAchTotal(appData.ach_intransit_total || 0);
      setAuditLog(histRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (id) => {
    if (settling) return;
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setSettling(id);
    try {
      const res = await api.patch(`/v1/approvals/${id}`, { status: "approved", managed_by: "Fleet Admin Node" });
      if (res.data.ok) {
        toast.success("Approved · ACH in-transit");
        setFadingIds((prev) => new Set([...prev, id]));
        setAchTotal((prev) => Math.round((prev + Number(row.amount)) * 100) / 100);
        setTimeout(() => {
          setRows((prev) => prev.map((r) => r.id === id ? { ...r, status: "approved" } : r));
          setFadingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
          setAuditLog((prev) => [{
            id,
            submitter_name: row.submitter_name,
            merchant: row.merchant || row.description,
            amount: row.amount,
            status: "approved",
            decided_at: new Date().toISOString(),
            decided_by: "Fleet Admin Node",
          }, ...prev]);
        }, 320);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enterprise plan required");
    } finally {
      setSettling(null);
    }
  };

  const handleDeclineConfirm = async (id) => {
    if (!declineReason || settling) return;
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setSettling(id);
    try {
      const res = await api.patch(`/v1/approvals/${id}`, {
        status: "denied",
        deny_reason: declineReason,
        managed_by: "Fleet Admin Node",
      });
      if (res.data.ok) {
        toast.warning("Declined · Audit trail updated");
        setDeclineOpen(null);
        setDeclineReason("");
        setFadingIds((prev) => new Set([...prev, id]));
        setTimeout(() => {
          setRows((prev) => prev.map((r) => r.id === id ? { ...r, status: "denied" } : r));
          setFadingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
          setAuditLog((prev) => [{
            id,
            submitter_name: row.submitter_name,
            merchant: row.merchant || row.description,
            amount: row.amount,
            status: "denied",
            deny_reason: declineReason,
            decided_at: new Date().toISOString(),
            decided_by: "Fleet Admin Node",
          }, ...prev]);
        }, 320);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enterprise plan required");
    } finally {
      setSettling(null);
    }
  };

  const pendingRows = rows.filter((r) => r.status === "pending");
  const approvedCount = rows.filter((r) => r.status === "approved").length;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div data-testid="approvals-kpi-pending" className="glass rounded-2xl p-5">
          <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Pending</div>
          <div className="text-3xl font-mono font-bold text-amber-400 mt-2">{pendingRows.length}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1 font-mono">Awaiting Review</div>
        </div>
        <div data-testid="approvals-kpi-approved" className="glass rounded-2xl p-5">
          <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Approved</div>
          <div className="text-3xl font-mono font-bold text-emerald-400 mt-2">{approvedCount}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1 font-mono">Cleared</div>
        </div>
        <div data-testid="approvals-kpi-ach" className="glass rounded-2xl p-5">
          <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">ACH In-Transit</div>
          <div className="text-3xl font-mono font-bold text-cyan-400 mt-2">${Number(achTotal).toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1 font-mono">Settling in 2 days</div>
        </div>
      </div>

      {/* Pending Queue Panel */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-cyan-400" />
            <h3 className="font-semibold text-white tracking-tight">Pending Expense Reviews</h3>
          </div>
          <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">{pendingRows.length} to review</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500 font-mono text-sm tracking-widest">LOADING QUEUE...</div>
        ) : pendingRows.length === 0 ? (
          <div data-testid="approvals-empty" className="p-10 text-center">
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-mono">ALL CAUGHT UP · 0 CLAIMS IN THE REIMBURSEMENT PIPELINE</div>
            <div className="mt-2 text-[9px] uppercase tracking-widest text-slate-600 font-mono">Submit an expense via the mobile scanner to begin</div>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {pendingRows.map((e) => {
              const isFading = fadingIds.has(e.id);
              const isDeclineOpen = declineOpen === e.id;
              const isHovered = hoverRowId === e.id;
              const initials = (e.submitter_name || "").split(" ").map((s) => s[0]).slice(0, 2).join("");
              const merchantName = e.merchant || e.description || "Unknown Merchant";
              const dateStr = e.created_at ? new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
              return (
                <div
                  key={e.id}
                  data-testid={`approval-row-${e.id}`}
                  className="px-6 py-4 flex flex-col gap-3"
                  style={{
                    opacity: isFading ? 0 : 1,
                    transform: isFading ? "translateX(-14px)" : "translateX(0)",
                    transition: "opacity 320ms ease, transform 320ms ease",
                  }}
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center text-emerald-300 text-sm font-semibold flex-shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate">
                        {e.submitter_name}
                        <span className="text-slate-500 font-mono text-xs"> · {e.submitter_email}</span>
                      </div>
                      <div className="relative inline-block">
                        <span
                          data-testid={`merchant-hover-${e.id}`}
                          className="text-sm text-cyan-400 cursor-pointer hover:text-cyan-300 underline decoration-dotted underline-offset-2 transition-colors"
                          onMouseEnter={() => setHoverRowId(e.id)}
                          onMouseLeave={() => setHoverRowId(null)}
                        >
                          {merchantName}
                        </span>
                        {isHovered && (
                          <div
                            data-testid={`receipt-preview-${e.id}`}
                            className="absolute left-0 top-7 w-72 bg-slate-900/95 backdrop-blur-md border border-cyan-500/30 rounded-xl p-3 shadow-2xl z-50"
                            style={{ animation: "approvalPreviewIn 180ms ease both" }}
                            onMouseEnter={() => setHoverRowId(e.id)}
                            onMouseLeave={() => setHoverRowId(null)}
                          >
                            <div className="text-[9px] uppercase tracking-widest text-cyan-400 font-mono mb-2.5 flex items-center gap-1.5">
                              <DollarSign size={10} /> Expense Document Preview
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-400">Merchant</span>
                                <span className="text-xs text-white font-semibold">{merchantName}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-400">Amount</span>
                                <span className="text-xs font-mono text-emerald-300 font-bold">${Number(e.amount).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-400">Category</span>
                                <span className="text-[10px] uppercase tracking-widest text-slate-300 bg-white/5 px-2 py-0.5 rounded-full">{e.category}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-slate-400">Submitted</span>
                                <span className="text-xs font-mono text-slate-300">{dateStr}</span>
                              </div>
                              <div className="flex justify-between items-start gap-2 pt-1.5 border-t border-white/5">
                                <span className="text-xs text-slate-400 flex-shrink-0">Note</span>
                                <span className="text-xs text-slate-300 text-right">{e.description}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-1.5 border-t border-white/5">
                              <div className="text-[9px] uppercase tracking-widest text-slate-600 font-mono">Status: Pending Review</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono font-bold text-white">${Number(e.amount).toFixed(2)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{e.category}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleApprove(e.id)}
                        disabled={!!settling}
                        data-testid={`approve-btn-${e.id}`}
                        className="h-9 px-3 rounded-lg bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                      >
                        <Check size={14} /> Approve
                      </button>
                      <button
                        onClick={() => { setDeclineOpen(isDeclineOpen ? null : e.id); setDeclineReason(""); }}
                        data-testid={`decline-btn-${e.id}`}
                        className="h-9 px-3 rounded-lg border border-red-500/30 hover:bg-red-500/10 hover:text-red-300 text-slate-300 text-sm flex items-center gap-1.5 transition-colors"
                      >
                        <X size={14} /> Decline
                      </button>
                    </div>
                  </div>
                  {isDeclineOpen && (
                    <div
                      data-testid={`decline-panel-${e.id}`}
                      className="ml-14 rounded-xl bg-red-950/20 border border-red-500/20 p-3 flex items-center gap-3 flex-wrap"
                      style={{ animation: "approvalPreviewIn 160ms ease both" }}
                    >
                      <select
                        value={declineReason}
                        onChange={(ev) => setDeclineReason(ev.target.value)}
                        data-testid={`decline-reason-select-${e.id}`}
                        className="flex-1 min-w-[200px] bg-slate-800 border border-white/10 rounded-lg px-3 h-9 text-sm text-white outline-none focus:border-red-500/50"
                      >
                        <option value="">Select decline reason...</option>
                        {DECLINE_REASONS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleDeclineConfirm(e.id)}
                        disabled={!declineReason || !!settling}
                        data-testid={`decline-confirm-${e.id}`}
                        className="h-9 px-4 rounded-lg bg-red-500/80 hover:bg-red-500 text-white font-semibold text-sm transition-colors disabled:opacity-40"
                      >
                        Confirm Decline
                      </button>
                      <button
                        onClick={() => { setDeclineOpen(null); setDeclineReason(""); }}
                        data-testid={`decline-cancel-${e.id}`}
                        className="h-9 px-3 rounded-lg border border-white/10 text-slate-400 text-sm hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Settled Audits Log */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock size={16} className="text-slate-400" />
            <h3 className="font-semibold text-white tracking-tight">Settled Audits Log</h3>
          </div>
          <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">{auditLog.length} records</span>
        </div>
        {auditLog.length === 0 ? (
          <div data-testid="audit-log-empty" className="p-8 text-center text-slate-600 font-mono text-xs tracking-widest uppercase">No Settled Records Yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="px-6 py-3 text-left text-[10px] uppercase tracking-widest text-slate-500 font-mono">Timestamp</th>
                  <th className="px-4 py-3 text-left text-[10px] uppercase tracking-widest text-slate-500 font-mono">Employee</th>
                  <th className="px-4 py-3 text-left text-[10px] uppercase tracking-widest text-slate-500 font-mono">Merchant</th>
                  <th className="px-4 py-3 text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Amount</th>
                  <th className="px-4 py-3 text-left text-[10px] uppercase tracking-widest text-slate-500 font-mono">Managed By Admin Node</th>
                  <th className="px-6 py-3 text-left text-[10px] uppercase tracking-widest text-slate-500 font-mono">Decision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {auditLog.map((entry) => (
                  <tr key={entry.id} data-testid={`audit-row-${entry.id}`} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-3 text-xs font-mono text-slate-400">
                      {entry.decided_at ? new Date(entry.decided_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-white">{entry.submitter_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{entry.merchant || entry.description || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-white">${Number(entry.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs text-slate-400 font-mono">{entry.decided_by || entry.managed_by || "Fleet Admin Node"}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-block text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full ${entry.status === "approved" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                        {entry.status === "approved" ? "Approved" : "Declined"}
                      </span>
                      {entry.deny_reason && (
                        <div className="text-[10px] text-slate-500 mt-0.5 max-w-[180px]">{entry.deny_reason}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Policy ----------------
function PolicyTab() {
  const [caps, setCaps] = useState({});
  const [violations, setViolations] = useState([]);
  useEffect(() => {
    api.get("/v1/policy-caps").then((r) => setCaps(r.data.caps || {})).catch(() => setCaps({}));
    api.get("/policy/violations").then((r) => setViolations(r.data || [])).catch(() => setViolations([]));
  }, []);

  const save = async (k, v) => {
    const next = { ...caps, [k]: Number(v) || 0 };
    setCaps(next);
    try {
      await api.post("/v1/policy-caps", { caps: next, policy_scope: "Global Fleet" });
      toast.success("Policy updated");
      const r = await api.get("/policy/violations");
      setViolations(r.data || []);
    } catch (e) {
      toast.error("Business plan required to edit caps");
    }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck size={18} className="text-emerald-400" />
          <h3 className="font-semibold text-white tracking-tight">Expense caps by category</h3>
        </div>
        <p className="text-slate-400 text-sm mb-5">AI boundary checks fire when a receipt or a single line-item exceeds these limits.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(caps).map(([k, v]) => (
            <label key={k} className="rounded-xl p-4 bg-white/[0.03] border border-white/5 hover:border-emerald-500/25 transition-colors">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{k.replace("_", " ")}</div>
              <div className="flex items-center gap-2">
                <span className="text-emerald-400 font-mono">$</span>
                <input
                  type="number"
                  defaultValue={v}
                  onBlur={(e) => e.target.value !== String(v) && save(k, e.target.value)}
                  data-testid={`cap-${k}`}
                  className="flex-1 bg-transparent outline-none text-white font-mono text-xl font-bold w-full"
                />
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <h3 className="font-semibold text-white tracking-tight">Policy violations</h3>
          <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">{violations.length} flagged</span>
        </div>
        {violations.length === 0 ? (
          <EnterpriseEmptyState label="COMPLIANCE CORE ACTIVE · POLICY CLEAN · 0 EXPOSURES FLAGGED" testid="policy-empty-scanner" />
        ) : (
          <div className="divide-y divide-white/5">
            {violations.map((v) => (
              <div key={v.receipt_id} className="px-6 py-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-white">{v.merchant}</div>
                  <div className="font-mono text-red-300 font-bold">${Number(v.total).toFixed(2)}</div>
                </div>
                <ul className="space-y-1 text-sm text-slate-300">
                  {v.violations.map((vi, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <AlertCircle size={12} className="text-amber-400" />
                      <span>{vi.message}</span>
                      <span className="ml-auto text-[10px] uppercase tracking-widest font-mono text-red-300">+${vi.over_by.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Virtual Cards ----------------
// ---------------- Smart Visa (isolated mock module) ----------------
const SMART_VISA_MOCK_MERCHANTS = [
  "Delta Airlines", "AWS Cloud", "Stripe Atlas", "Notion Labs", "Sequoia Hotels",
  "Shell Fuel Stop", "OpenAI · API", "WeWork Hub", "Uber for Business",
];
const SMART_VISA_MCC = [
  { value: "", label: "Any category" },
  { value: "software", label: "5734 · Software" },
  { value: "travel", label: "4722 · Travel" },
  { value: "meals", label: "5812 · Meals" },
  { value: "fuel", label: "5541 · Fuel Stations" },
  { value: "office", label: "5943 · Office Supplies" },
];

function SmartVisaModule() {
  const [cap, setCap] = useState(2500);
  const [mcc, setMcc] = useState("software");
  const [issued, setIssued] = useState(false);
  const [holder, setHolder] = useState("Alex Rivera");
  const [phone, setPhone] = useState("+1 (415) 555-0134");
  const [swipes, setSwipes] = useState([]);
  const [autoLoop, setAutoLoop] = useState(false);
  const [flash, setFlash] = useState(false);
  const [cardUsageType, setCardUsageType] = useState("recurring");
  const [geoFence, setGeoFence] = useState("US");
  const [smsAlerts, setSmsAlerts] = useState(true);
  const [cardId, setCardId] = useState(null);
  const [issuingCard, setIssuingCard] = useState(false);

  // Mock card number (deterministic, non-issued)
  const cardNumber = "4242 4269 8817 3050";
  const last4 = cardNumber.slice(-4);
  const cvv = "•••";

  const fireSwipe = async (opts = {}) => {
    if (!issued) {
      toast.error("Issue the card first — toggle 'Issue New Employee Card'.");
      return;
    }
    const merchant = opts.merchant || SMART_VISA_MOCK_MERCHANTS[Math.floor(Math.random() * SMART_VISA_MOCK_MERCHANTS.length)];
    const amount = opts.amount || +(Math.random() * Math.min(cap, 480) + 12).toFixed(2);
    const ts = new Date();
    let authId = `AUTH_${ts.getTime().toString(36).toUpperCase()}`;
    let approved = true;
    let declineReason = null;
    if (cardId) {
      try {
        const { data } = await api.post("/v1/cards/authorize", {
          card_id: cardId,
          merchant,
          amount,
          mcc: mcc || null,
          country: geoFence || "US",
        });
        authId = data.auth_id;
        approved = data.approved;
        declineReason = data.declined_reason;
      } catch {
        // backend auth service unreachable — local evaluation fallback
      }
    }
    const evt = {
      id: authId,
      merchant,
      amount,
      time: ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
      mcc: mcc || "any",
      approved,
      declineReason,
    };
    setSwipes((s) => [evt, ...s].slice(0, 6));
    setFlash(true);
    setTimeout(() => setFlash(false), 900);
    if (approved) {
      toast.success(
        `AUTH ${authId} · ${merchant} $${amount.toFixed(2)}${smsAlerts ? ` · SMS → ${phone}` : ""}`,
        { duration: 4200 }
      );
    } else {
      toast.error(
        `DECLINED · ${declineReason} · ${merchant} $${amount.toFixed(2)}`,
        { duration: 4200 }
      );
    }
  };

  // Authorization evaluator loop — 1 token evaluation every 6s while enabled
  useEffect(() => {
    if (!autoLoop || !issued) return;
    const t = setInterval(() => { fireSwipe(); }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [autoLoop, issued, cap, mcc, phone, cardId, geoFence, smsAlerts]);

  return (
    <div className="glass rounded-2xl p-6 border border-emerald-500/20" data-testid="smart-visa-module">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
          <Radio size={16} className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">Smart Visa · Beta</div>
          <h3 className="font-semibold text-white tracking-tight text-lg">Corporate Card Designer</h3>
        </div>
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
          data-testid="visa-mainnet-badge"
          style={{
            background: "rgba(16,185,129,0.12)",
            border: "1px solid rgba(16,185,129,0.38)",
            boxShadow: "0 0 12px -4px rgba(16,185,129,0.5)",
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ boxShadow: "0 0 6px rgba(16,185,129,1)" }} />
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">🟢 VISA ISSUING MAINNET • PRODUCTION</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ============ 3D CARD PREVIEW ============ */}
        <div className="flex items-center justify-center py-2">
          <div className="smart-visa-scene" style={{ perspective: "1200px" }}>
            <div
              data-testid="smart-visa-card"
              className={`smart-visa-card relative rounded-2xl overflow-hidden ${flash ? "smart-visa-flash" : ""}`}
              style={{
                width: 340,
                height: 210,
                background:
                  "linear-gradient(135deg, #064e3b 0%, #0f172a 55%, #020617 100%)",
                boxShadow:
                  "0 30px 60px -20px rgba(16,185,129,0.4), 0 0 0 1px rgba(16,185,129,0.25) inset",
                transformStyle: "preserve-3d",
                transition: "transform 400ms cubic-bezier(.16,1,.3,1), box-shadow 400ms",
              }}
              onMouseMove={(e) => {
                const el = e.currentTarget;
                const r = el.getBoundingClientRect();
                const x = (e.clientX - r.left) / r.width - 0.5;
                const y = (e.clientY - r.top) / r.height - 0.5;
                el.style.transform = `rotateY(${x * 18}deg) rotateX(${-y * 14}deg) translateZ(0)`;
                const gloss = el.querySelector(".sv-gloss");
                if (gloss) gloss.style.background = `radial-gradient(600px circle at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%, rgba(255,255,255,0.18), transparent 40%)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "rotateY(0deg) rotateX(0deg)";
                const gloss = e.currentTarget.querySelector(".sv-gloss");
                if (gloss) gloss.style.background = "transparent";
              }}
            >
              {/* Grid overlay */}
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(rgba(16,185,129,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.08) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
              {/* Reflective gloss layer */}
              <div className="sv-gloss absolute inset-0 pointer-events-none" style={{ transition: "background 200ms" }} />
              {/* Top row */}
              <div className="relative flex items-start justify-between p-5">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-300">Receipt Sheets</div>
                  <div className="text-white font-semibold tracking-tight mt-0.5">Smart Visa</div>
                </div>
                <Wifi size={18} className="text-emerald-300 rotate-90" />
              </div>
              {/* Chip */}
              <div className="relative px-5">
                <div className="w-11 h-8 rounded-md" style={{
                  background: "linear-gradient(135deg, #fde68a 0%, #d97706 60%, #92400e 100%)",
                  boxShadow: "inset 0 0 8px rgba(0,0,0,0.5)",
                }}>
                  <div className="w-full h-full grid grid-cols-3 grid-rows-3 gap-[1px] p-[3px]">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <div key={i} className="bg-amber-900/40 rounded-[1px]" />
                    ))}
                  </div>
                </div>
              </div>
              {/* Number */}
              <div className="relative px-5 mt-3 font-mono text-white text-[17px] tracking-[0.25em]" data-testid="smart-visa-number">
                {cardNumber}
              </div>
              {/* Bottom row */}
              <div className="relative flex items-end justify-between px-5 pt-2">
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300/70">Cardholder</div>
                  <div className="text-white text-xs font-medium">{issued ? holder.toUpperCase() : "NOT ISSUED"}</div>
                </div>
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300/70">Exp · CVV</div>
                  <div className="text-white text-xs font-mono">12/29 · {cvv}</div>
                </div>
                {/* VISA wordmark */}
                <div className="text-white font-black italic tracking-tight text-xl" style={{ fontFamily: "Georgia, serif", textShadow: "0 1px 0 rgba(0,0,0,0.4)" }}>VISA</div>
              </div>
              {/* Frozen badge */}
              {!issued && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-950/55 backdrop-blur-[1px]">
                  <span className="text-[10px] font-mono uppercase tracking-[0.35em] text-slate-300 px-3 py-1 rounded-full border border-white/15">
                    <Lock size={10} className="inline -mt-0.5 mr-1" /> Awaiting issuance
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ============ CONTROL TARGET FORM ============ */}
        <div className="space-y-4" data-testid="smart-visa-form">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">
              Set Spending Cap ($)
            </label>
            <div className="flex items-center gap-2">
              <DollarSign size={14} className="text-emerald-400" />
              <input
                type="number"
                value={cap}
                min={100}
                step={100}
                onChange={(e) => setCap(Math.max(100, Number(e.target.value) || 0))}
                data-testid="smart-visa-cap-input"
                className="flex-1 h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50 font-mono"
              />
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">USD / mo</span>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">
              Select Merchant Category Lock
            </label>
            <select
              value={mcc}
              onChange={(e) => setMcc(e.target.value)}
              data-testid="smart-visa-mcc-select"
              className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
            >
              {SMART_VISA_MCC.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Cardholder</label>
              <input
                value={holder}
                onChange={(e) => setHolder(e.target.value)}
                data-testid="smart-visa-holder-input"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Mobile (SMS)</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="smart-visa-phone-input"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50 font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Card Usage Type</label>
              <select
                value={cardUsageType}
                onChange={(e) => setCardUsageType(e.target.value)}
                data-testid="smart-visa-usage-type-select"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
              >
                <option value="recurring">Recurring</option>
                <option value="single_use">Single-Use</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Geo-Fence Perimeter</label>
              <input
                value={geoFence}
                onChange={(e) => setGeoFence(e.target.value)}
                placeholder="e.g. US, US,CA,GB"
                data-testid="smart-visa-geo-fence-input"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50 font-mono text-xs"
              />
            </div>
          </div>

          <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] cursor-pointer">
            <div>
              <div className="text-sm font-semibold text-white">SMS Transaction Alerts</div>
              <div className="text-[11px] text-slate-400">Fire an SMS metadata payload to {phone || "the registered number"} on every auth event.</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={smsAlerts}
              onClick={(e) => { e.preventDefault(); setSmsAlerts((v) => !v); }}
              data-testid="smart-visa-sms-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${smsAlerts ? "bg-cyan-500" : "bg-slate-600"}`}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: smsAlerts ? "translateX(24px)" : "translateX(0)" }}
              />
            </button>
          </label>

          <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] cursor-pointer">
            <div>
              <div className="text-sm font-semibold text-white">Issue New Employee Card</div>
              <div className="text-[11px] text-slate-400">Provision card via VISA Issuing Mainnet · unlock the authorization evaluator.</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={issued}
              disabled={issuingCard}
              onClick={async () => {
                if (issued) {
                  setIssued(false);
                  setCardId(null);
                  setAutoLoop(false);
                  toast("Card revoked · all future auth requests declined", { icon: "🔒" });
                } else {
                  setIssuingCard(true);
                  try {
                    const { data } = await api.post("/v1/cards/create", {
                      name: holder,
                      spend_limit: cap,
                      card_usage_type: cardUsageType,
                      category_lock: mcc || null,
                      geo_fence: geoFence || null,
                      sms_alerts_enabled: smsAlerts,
                    });
                    setCardId(data.id);
                    setIssued(true);
                    toast.success(
                      `Card ${data.card_last4} issued to ${holder} · ${cardUsageType === "single_use" ? "SINGLE-USE" : "RECURRING"} · ${smsAlerts ? `SMS → ${phone}` : "SMS alerts off"}`,
                      { duration: 5000 }
                    );
                  } catch (e) {
                    const msg = e?.response?.data?.detail || "Card issuance failed — check plan tier";
                    toast.error(msg);
                  } finally {
                    setIssuingCard(false);
                  }
                }
              }}
              data-testid="smart-visa-issue-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors disabled:opacity-60 flex-shrink-0 ${issued ? "bg-emerald-500" : issuingCard ? "bg-slate-500" : "bg-slate-600"}`}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: issued ? "translateX(24px)" : "translateX(0)" }}
              />
            </button>
          </label>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={() => fireSwipe()}
              disabled={!issued || issuingCard}
              data-testid="smart-visa-fire-swipe-btn"
              className="btn-neon h-10 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Zap size={14} /> Evaluate Authorization
            </button>
            <button
              onClick={() => setAutoLoop((v) => !v)}
              disabled={!issued}
              data-testid="smart-visa-autoloop-btn"
              className={`h-10 px-4 rounded-lg text-sm font-mono uppercase tracking-widest border transition-colors ${autoLoop ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"} disabled:opacity-40`}
            >
              {autoLoop ? "Auto: ON · 6s" : "Auto: OFF"}
            </button>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 ml-auto">
              {cardId ? `Live · ${cardId.slice(-8).toUpperCase()}` : `Card ····${last4}`}
            </span>
          </div>
        </div>
      </div>

      {/* ============ LIVE AUTHORIZATION FEED ============ */}
      <div className="mt-6 border-t border-white/5 pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">Live Authorization Feed · VISA Mainnet</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            {swipes.length} auth{swipes.length === 1 ? "" : "s"}
          </div>
        </div>
        {swipes.length === 0 ? (
          <div className="text-slate-500 text-sm py-4 text-center border border-dashed border-white/10 rounded-lg" data-testid="smart-visa-empty-feed">
            No authorizations yet — fire a card swipe to evaluate against the VISA token evaluator.
          </div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="smart-visa-signal-list">
            {swipes.map((s) => (
              <li key={s.id} className="py-2.5 flex items-center gap-3">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{
                    background: s.approved === false ? "#f87171" : "#34d399",
                    boxShadow: s.approved === false ? "0 0 8px rgba(248,113,113,0.9)" : "0 0 8px rgba(52,211,153,0.9)",
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate flex items-center gap-2 flex-wrap">
                    <span>{s.merchant}</span>
                    <span className="text-slate-500 font-mono text-xs">· MCC {s.mcc}</span>
                    {s.declineReason && (
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 text-red-300 flex-shrink-0">{s.declineReason}</span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono" style={{ color: s.approved === false ? "#fca5a5" : "#94a3b8" }}>
                    {s.approved === false ? "DECLINED" : `AUTH ${s.id}`} · {s.time}
                    {smsAlerts && s.approved !== false && <span className="text-slate-600"> · SMS {phone}</span>}
                  </div>
                </div>
                <div className="font-mono font-semibold" style={{ color: s.approved === false ? "#f87171" : "#34d399" }}>
                  ${s.amount.toFixed(2)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>      {/* Scoped hover-glow keyframes */}
      <style>{`
        .smart-visa-card:hover { box-shadow: 0 40px 80px -20px rgba(16,185,129,0.55), 0 0 0 1px rgba(16,185,129,0.5) inset !important; }
        @keyframes smart-visa-flash-kf {
          0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.9), 0 30px 60px -20px rgba(16,185,129,0.4); }
          50%  { box-shadow: 0 0 0 14px rgba(16,185,129,0), 0 30px 60px -20px rgba(16,185,129,0.9); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0), 0 30px 60px -20px rgba(16,185,129,0.4); }
        }
        .smart-visa-flash { animation: smart-visa-flash-kf 900ms ease-out; }
      `}</style>
    </div>
  );
}

// ---------------- Smart Visa Issuing Management Panel (frontend-only) ----------------
const SEED_EMPLOYEE_CARDS = [
  { id: "ec_alex",   name: "Alex Rivera",     type: "Virtual",  status: "active", spend: 1247.55, limit: 2500, last4: "3050", team: "Marketing" },
  { id: "ec_priya",  name: "Priya Nair",      type: "Physical", status: "active", spend:  342.10, limit: 1500, last4: "8891", team: "Design" },
  { id: "ec_marcus", name: "Marcus Chen",     type: "Virtual",  status: "frozen", spend:  890.44, limit: 3000, last4: "2277", team: "Engineering" },
  { id: "ec_dana",   name: "Dana Kowalski",   type: "Virtual",  status: "active", spend:   55.19, limit:  800, last4: "9014", team: "Ops" },
  { id: "ec_julien", name: "Julien Fontaine", type: "Physical", status: "active", spend: 2088.72, limit: 4000, last4: "6432", team: "Sales" },
];

function IssueCardModal({ open, onClose, onIssue }) {
  const [name, setName] = useState("");
  const [cap, setCap] = useState(1500);
  const [type, setType] = useState("Virtual");
  const [modalCardUsageType, setModalCardUsageType] = useState("recurring");
  const [modalGeoFence, setModalGeoFence] = useState("US");
  const [modalSmsAlerts, setModalSmsAlerts] = useState(true);

  useEffect(() => {
    if (!open) {
      setName("");
      setCap(1500);
      setType("Virtual");
      setModalCardUsageType("recurring");
      setModalGeoFence("US");
      setModalSmsAlerts(true);
    }
  }, [open]);

  if (!open) return null;
  const submit = () => {
    if (!name.trim()) return toast.error("Employee name required");
    if (!cap || Number(cap) < 100) return toast.error("Cap must be at least $100");
    onIssue({
      id: `ec_${Date.now().toString(36)}`,
      name: name.trim(),
      type,
      cardUsageType: modalCardUsageType,
      geoFence: modalGeoFence,
      smsAlerts: modalSmsAlerts,
      status: "active",
      spend: 0,
      limit: Number(cap),
      last4: String(Math.floor(1000 + Math.random() * 9000)).padStart(4, "0").slice(-4),
      team: "Unassigned",
    });
    onClose();
  };

  return (
    <div
      data-testid="issue-card-modal"
      className="fixed inset-0 z-[9500] flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.75)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(2,6,23,0.98) 100%)",
          border: "1px solid rgba(16,185,129,0.35)",
          boxShadow: "0 40px 80px -20px rgba(16,185,129,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-emerald-400" />
            <div className="text-white font-semibold tracking-tight">Issue new employee card</div>
          </div>
          <button onClick={onClose} data-testid="issue-modal-close" className="text-slate-400 hover:text-white p-1 rounded"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Employee name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jamie Ortega"
              data-testid="issue-modal-name-input"
              className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Spending Cap ($)</label>
              <input
                type="number"
                value={cap}
                min={100}
                step={100}
                onChange={(e) => setCap(e.target.value)}
                data-testid="issue-modal-cap-input"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50 font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Card type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                data-testid="issue-modal-type-select"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
              >
                <option value="Virtual">Virtual</option>
                <option value="Physical">Physical</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Card Usage Type</label>
              <select
                value={modalCardUsageType}
                onChange={(e) => setModalCardUsageType(e.target.value)}
                data-testid="issue-modal-usage-type-select"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
              >
                <option value="recurring">Recurring</option>
                <option value="single_use">Single-Use</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Geo-Fence</label>
              <input
                value={modalGeoFence}
                onChange={(e) => setModalGeoFence(e.target.value)}
                placeholder="US, US,CA,GB…"
                data-testid="issue-modal-geo-fence-input"
                className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50 font-mono text-xs"
              />
            </div>
          </div>
          <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] cursor-pointer">
            <div>
              <div className="text-sm font-semibold text-white">SMS Transaction Alerts</div>
              <div className="text-[11px] text-slate-400">Fire a metadata payload to the registered mobile on each auth event.</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={modalSmsAlerts}
              onClick={(e) => { e.preventDefault(); setModalSmsAlerts((v) => !v); }}
              data-testid="issue-modal-sms-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${modalSmsAlerts ? "bg-cyan-500" : "bg-slate-600"}`}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: modalSmsAlerts ? "translateX(24px)" : "translateX(0)" }}
              />
            </button>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="issue-modal-cancel" className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm">Cancel</button>
          <button
            onClick={submit}
            data-testid="issue-modal-submit"
            className="btn-neon h-10 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2"
          >
            <Plus size={14} /> Issue card
          </button>
        </div>
      </div>
    </div>
  );
}

function SmartVisaIssuingPanel() {
  const [cardsList, setCardsList] = useState(SEED_EMPLOYEE_CARDS);
  const [selectedId, setSelectedId] = useState(SEED_EMPLOYEE_CARDS[0].id);
  const [modalOpen, setModalOpen] = useState(false);

  // Load persisted cards from DB on mount; fall back to seed if unauthenticated
  useEffect(() => {
    api.get("/v1/cards/ledger")
      .then((res) => {
        const apiCards = (res.data?.cards || []).map((c) => ({
          id: c.id,
          name: c.name,
          type: c.card_usage_type === "single_use" ? "Single-Use" : "Virtual",
          status: c.frozen ? "frozen" : (c.status || "active"),
          spend: Number(c.spent || 0),
          limit: Number(c.spend_limit || 0),
          last4: c.card_last4 || "????",
          team: "Unassigned",
          mcc: c.category_lock || "",
        }));
        if (apiCards.length > 0) {
          setCardsList(apiCards);
          setSelectedId(apiCards[0].id);
        }
      })
      .catch(() => {});
  }, []);

  const selected = cardsList.find((c) => c.id === selectedId) || cardsList[0];
  const totalSpend = cardsList.reduce((s, c) => s + c.spend, 0);
  const totalLimit = cardsList.reduce((s, c) => s + c.limit, 0);
  const activeCount = cardsList.filter((c) => c.status === "active").length;
  const frozenCount = cardsList.length - activeCount;

  const issue = async (rec) => {
    setCardsList((cs) => [rec, ...cs]);
    setSelectedId(rec.id);
    toast.success(`Card issued to ${rec.name} · ${rec.type} · limit $${rec.limit}`);
    try {
      const { data } = await api.post("/v1/cards/create", {
        name: rec.name,
        spend_limit: rec.limit,
        card_usage_type: rec.cardUsageType || "recurring",
        category_lock: rec.mcc || null,
        geo_fence: rec.geoFence || "US",
        sms_alerts_enabled: rec.smsAlerts !== false,
      });
      const realId = data.id;
      const realLast4 = data.card_last4;
      setCardsList((cs) => cs.map((c) => c.id === rec.id ? { ...c, id: realId, last4: realLast4 } : c));
      setSelectedId(realId);
    } catch {
      // Optimistic UI retained — local card stays visible
    }
  };

  const toggleFreeze = async (id) => {
    const current = cardsList.find((c) => c.id === id);
    if (!current) return;
    const nextFrozen = current.status !== "frozen";
    const nextStatus = nextFrozen ? "frozen" : "active";
    setCardsList((cs) => cs.map((c) => c.id !== id ? c : { ...c, status: nextStatus }));
    const smsPayload = nextFrozen
      ? `FREEZE ALERT · Card ····${current.last4} (${current.name}) · all authorization requests DECLINED · ${new Date().toLocaleTimeString()}`
      : `REACTIVATION ALERT · Card ····${current.last4} (${current.name}) · authorization processing RESUMED · ${new Date().toLocaleTimeString()}`;
    toast(smsPayload, { icon: nextFrozen ? "❄️" : "✅", duration: 5000 });
    try {
      await api.post("/v1/cards/freeze", { card_id: id, frozen: nextFrozen });
    } catch {
      // Freeze state persisted optimistically — DB sync retried on next load
    }
  };

  const isFrozen = selected?.status === "frozen";

  return (
    <div className="glass rounded-2xl p-6 border border-emerald-500/20" data-testid="smart-visa-issuing-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
          <CreditCard size={16} className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">Smart Visa · Issuing</div>
          <h3 className="font-semibold text-white tracking-tight text-lg">Virtual Card Issuing Control</h3>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          data-testid="issuing-new-card-btn"
          className="btn-neon h-10 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2"
        >
          <Plus size={14} /> Issue New Card
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Cards Issued</div>
          <div className="text-white font-semibold text-xl mt-0.5">{cardsList.length}</div>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Active · Frozen</div>
          <div className="text-white font-semibold text-xl mt-0.5"><span className="text-emerald-300">{activeCount}</span> · <span className="text-red-300">{frozenCount}</span></div>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">MTD Spend</div>
          <div className="text-white font-semibold text-xl mt-0.5 font-mono">${totalSpend.toFixed(2)}</div>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Total Limits</div>
          <div className="text-white font-semibold text-xl mt-0.5 font-mono">${totalLimit.toFixed(2)}</div>
        </div>
      </div>

      {/* 3D hero card */}
      <div className="mb-6 flex items-center justify-center py-4" style={{ perspective: "1400px" }}>
        <div
          data-testid="issuing-hero-card"
          className={`iss-card relative rounded-2xl overflow-hidden ${isFrozen ? "iss-card-frozen" : ""}`}
          style={{
            width: 380,
            height: 232,
            background: "linear-gradient(140deg, #022c22 0%, #064e3b 25%, #0f172a 60%, #020617 100%)",
            border: "1px solid rgba(16,185,129,0.4)",
            boxShadow: isFrozen
              ? "0 20px 40px -20px rgba(148,163,184,0.4), 0 0 0 1px rgba(148,163,184,0.25) inset"
              : "0 40px 80px -20px rgba(16,185,129,0.55), 0 0 0 1px rgba(16,185,129,0.4) inset",
            transformStyle: "preserve-3d",
            transition: "transform 400ms cubic-bezier(.16,1,.3,1), box-shadow 400ms, filter 400ms",
            filter: isFrozen ? "grayscale(0.85) brightness(0.55)" : "none",
          }}
          onMouseMove={(e) => {
            if (isFrozen) return;
            const el = e.currentTarget;
            const r = el.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width - 0.5;
            const y = (e.clientY - r.top) / r.height - 0.5;
            el.style.transform = `rotateY(${x * 20}deg) rotateX(${-y * 16}deg) translateZ(0)`;
            const gloss = el.querySelector(".iss-gloss");
            if (gloss) gloss.style.background = `radial-gradient(700px circle at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%, rgba(255,255,255,0.20), transparent 45%)`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "rotateY(0deg) rotateX(0deg)";
            const gloss = e.currentTarget.querySelector(".iss-gloss");
            if (gloss) gloss.style.background = "transparent";
          }}
        >
          {/* subtle grid */}
          <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "linear-gradient(rgba(16,185,129,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.10) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          {/* pulsing holographic VISA watermark */}
          <div className="iss-holo absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="iss-holo-text" style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontWeight: 900 }}>VISA</span>
          </div>
          {/* mouse gloss */}
          <div className="iss-gloss absolute inset-0 pointer-events-none" style={{ transition: "background 200ms" }} />
          {/* top row */}
          <div className="relative flex items-start justify-between p-5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-300">Receipt Sheets</div>
              <div className="text-white font-semibold tracking-tight mt-0.5">Smart Visa · {selected?.type || "Virtual"}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-1">{selected?.team}</div>
            </div>
            <Wifi size={18} className="text-emerald-300 rotate-90" />
          </div>
          {/* neon-emerald chip */}
          <div className="relative px-5">
            <div
              className="w-12 h-9 rounded-md relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #10B981 0%, #059669 55%, #064e3b 100%)",
                boxShadow: isFrozen ? "none" : "0 0 18px rgba(16,185,129,0.75), inset 0 0 10px rgba(0,0,0,0.4)",
              }}
            >
              <div className="w-full h-full grid grid-cols-3 grid-rows-3 gap-[1px] p-[3px]">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="rounded-[1px]" style={{ background: "rgba(2,44,34,0.6)" }} />
                ))}
              </div>
              {!isFrozen && <div className="iss-chip-shimmer absolute inset-0 pointer-events-none" />}
            </div>
          </div>
          {/* number */}
          <div className="relative px-5 mt-3 font-mono text-white text-[18px] tracking-[0.28em]" data-testid="issuing-hero-number">
            •••• •••• •••• {selected?.last4 || "0000"}
          </div>
          {/* bottom row */}
          <div className="relative flex items-end justify-between px-5 pt-2">
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300/70">Cardholder</div>
              <div className="text-white text-xs font-medium">{(selected?.name || "").toUpperCase()}</div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300/70">Limit</div>
              <div className="text-white text-xs font-mono">${Number(selected?.limit || 0).toFixed(0)}</div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300/70">Spent</div>
              <div className="text-white text-xs font-mono">${Number(selected?.spend || 0).toFixed(2)}</div>
            </div>
          </div>
          {isFrozen && (
            <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded bg-red-500/20 border border-red-500/40 text-red-300">
              <Lock size={10} /> Frozen
            </div>
          )}
        </div>
      </div>

      {/* Employee grid */}
      <div className="rounded-xl overflow-hidden border border-white/5" data-testid="issuing-employee-grid">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-white/[0.03] text-[10px] font-mono uppercase tracking-widest text-slate-400">
          <div className="col-span-3">Employee</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">MTD Spend</div>
          <div className="col-span-2 text-right">Limit</div>
          <div className="col-span-1 text-right">Ctrl</div>
        </div>
        <ul className="divide-y divide-white/5">
          {cardsList.map((c) => {
            const pct = Math.min(100, (c.spend / (c.limit || 1)) * 100);
            const isSel = c.id === selectedId;
            const rowFrozen = c.status === "frozen";
            return (
              <li
                key={c.id}
                data-testid={`issuing-row-${c.id}`}
                onClick={() => setSelectedId(c.id)}
                className={`grid grid-cols-12 gap-2 items-center px-4 py-3 cursor-pointer transition-colors ${isSel ? "bg-emerald-500/[0.06]" : "hover:bg-white/[0.03]"}`}
              >
                <div className="col-span-3 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 truncate">····{c.last4} · {c.team}</div>
                </div>
                <div className="col-span-2 text-slate-300 text-sm">{c.type}</div>
                <div className="col-span-2">
                  <span
                    data-testid={`issuing-status-${c.id}`}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${
                      rowFrozen
                        ? "bg-red-500/15 border-red-500/40 text-red-300"
                        : "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    }`}
                  >
                    {rowFrozen ? "Frozen" : "Active"}
                  </span>
                </div>
                <div className="col-span-2 text-right">
                  <div className="text-white font-mono text-sm">${c.spend.toFixed(2)}</div>
                  <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: rowFrozen ? "#94a3b8" : (pct > 90 ? "#f59e0b" : "#10B981") }} />
                  </div>
                </div>
                <div className="col-span-2 text-right text-slate-300 font-mono text-sm">${c.limit.toFixed(0)}</div>
                <div className="col-span-1 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFreeze(c.id); }}
                    data-testid={`freeze-btn-${c.id}`}
                    title={rowFrozen ? "Unfreeze card · resume authorization" : "Freeze card · intercept all auth requests"}
                    className="relative overflow-hidden h-8 px-2.5 rounded-lg text-[9px] font-mono uppercase tracking-widest transition-all active:scale-95 hover:-translate-y-px flex items-center gap-1"
                    style={rowFrozen ? {
                      background: "rgba(239,68,68,0.1)",
                      border: "1px solid rgba(239,68,68,0.45)",
                      color: "#fca5a5",
                      boxShadow: "0 0 14px -6px rgba(239,68,68,0.6)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                    } : {
                      background: "rgba(6,182,212,0.07)",
                      border: "1px solid rgba(6,182,212,0.3)",
                      color: "#67e8f9",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                    }}
                  >
                    {rowFrozen ? <><Flame size={10} /> Thaw</> : <><Snowflake size={10} /> Freeze</>}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <IssueCardModal open={modalOpen} onClose={() => setModalOpen(false)} onIssue={issue} />

      {/* scoped styles: holographic VISA + chip shimmer + frozen state */}
      <style>{`
        .iss-holo-text {
          font-size: 90px;
          letter-spacing: -0.02em;
          background: linear-gradient(120deg, #10B981 0%, #22d3ee 20%, #a78bfa 40%, #f472b6 60%, #10B981 80%);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          opacity: 0.14;
          animation: iss-holo-flow 6s ease-in-out infinite, iss-holo-pulse 3.2s ease-in-out infinite;
          text-shadow: 0 0 30px rgba(16,185,129,0.15);
        }
        @keyframes iss-holo-flow {
          0%   { background-position:   0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position:   0% 50%; }
        }
        @keyframes iss-holo-pulse {
          0%, 100% { opacity: 0.10; transform: scale(1.00); }
          50%      { opacity: 0.22; transform: scale(1.04); }
        }
        .iss-card-frozen .iss-holo-text { animation: none; opacity: 0.05; }
        .iss-chip-shimmer {
          background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%);
          background-size: 250% 100%;
          animation: iss-chip-shine 3.6s ease-in-out infinite;
        }
        @keyframes iss-chip-shine {
          0%   { background-position: 200% 0; }
          100% { background-position: -100% 0; }
        }
      `}</style>
    </div>
  );
}

function CardsTab() {
  const [cards, setCards] = useState([]);
  const [form, setForm] = useState({ name: "", spend_limit: 500, category_lock: "" });
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState({});

  const load = () => api.get("/cards").then((r) => setCards(r.data || [])).catch(() => setCards([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Card name required");
    setCreating(true);
    try {
      await api.post("/cards", {
        name: form.name,
        spend_limit: Number(form.spend_limit),
        category_lock: form.category_lock || null,
      });
      toast.success("Virtual card issued");
      setForm({ name: "", spend_limit: 500, category_lock: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Business plan required to issue cards");
    } finally { setCreating(false); }
  };

  const toggleFreeze = async (c) => {
    try {
      await api.patch(`/cards/${c.id}`, { frozen: !c.frozen });
      load();
    } catch { toast.error("Business plan required"); }
  };
  const del = async (c) => {
    if (!confirm(`Delete card ${c.name}?`)) return;
    try {
      await api.delete(`/cards/${c.id}`);
      load();
    } catch { toast.error("Business plan required"); }
  };

  return (
    <div className="space-y-6">
      <SmartVisaModule />
      <SmartVisaIssuingPanel />
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <CreditCard size={18} className="text-emerald-400" />
          <h3 className="font-semibold text-white tracking-tight">Issue a virtual card</h3>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Card name (e.g. Marketing)"
            data-testid="card-name-input"
            className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
          />
          <input
            type="number"
            value={form.spend_limit}
            onChange={(e) => setForm({ ...form, spend_limit: e.target.value })}
            placeholder="Spend limit $"
            data-testid="card-limit-input"
            className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50 font-mono"
          />
          <select
            value={form.category_lock}
            onChange={(e) => setForm({ ...form, category_lock: e.target.value })}
            data-testid="card-category-select"
            className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50"
          >
            <option value="">Any category</option>
            <option value="software">Software</option>
            <option value="travel">Travel</option>
            <option value="meals">Meals</option>
            <option value="office_supplies">Office Supplies</option>
            <option value="transportation">Transportation</option>
          </select>
          <button
            onClick={create}
            disabled={creating}
            data-testid="issue-card-btn"
            className="btn-neon h-11 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          ><Plus size={16} /> {creating ? "Issuing…" : "Issue card"}</button>
        </div>
      </div>

      {cards.length === 0 ? (
        <div className="glass rounded-2xl p-14 text-center">
          <CreditCard size={36} className="text-slate-600 mx-auto mb-3" />
          <div className="text-slate-400">No virtual cards yet.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {cards.map((c) => {
            const pct = Math.min(100, (Number(c.spent) / Number(c.spend_limit || 1)) * 100);
            const revealed = reveal[c.id];
            return (
              <div key={c.id} data-testid={`card-${c.id}`} className={`relative rounded-2xl p-5 overflow-hidden border ${c.frozen ? "opacity-60 border-slate-600" : "border-emerald-500/25"}`} style={{ background: "linear-gradient(135deg, #064e3b 0%, #0f172a 60%, #0f172a 100%)", boxShadow: c.frozen ? "none" : "0 0 30px -10px rgba(16,185,129,0.35)" }}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-mono">RS · Virtual</div>
                    <div className="mt-1 font-semibold text-white text-lg">{c.name}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleFreeze(c)} data-testid={`freeze-${c.id}`} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" title={c.frozen ? "Unfreeze" : "Freeze"}>
                      {c.frozen ? <Flame size={14} /> : <Snowflake size={14} />}
                    </button>
                    <button onClick={() => del(c)} className="p-2 rounded-lg hover:bg-red-500/10 hover:text-red-300 text-slate-300"><Trash2 size={14} /></button>
                  </div>
                </div>

                <div className="mt-8 font-mono text-lg tracking-widest text-white cursor-pointer" onClick={() => setReveal({ ...reveal, [c.id]: !revealed })}>
                  {revealed
                    ? c.card_full.replace(/(.{4})/g, "$1 ").trim()
                    : `•••• •••• •••• ${c.card_last4}`}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs font-mono text-slate-400">
                  <span>EXP {c.expires}</span>
                  <span>CVV {revealed ? c.cvv : "•••"}</span>
                  <span className="uppercase tracking-widest">{c.category_lock || "any"}</span>
                </div>
                <div className="mt-4">
                  <div className="flex items-baseline justify-between text-xs mb-1.5">
                    <span className="text-slate-400">${Number(c.spent || 0).toFixed(2)} spent</span>
                    <span className="text-emerald-300 font-mono">${Number(c.spend_limit).toFixed(2)} limit</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                {c.frozen && (
                  <div className="absolute top-3 right-16 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-slate-950/70 text-slate-300 border border-slate-500/40">
                    <Lock size={10} /> Frozen
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------- Mileage ----------------
function MileageTab() {
  const { nudge, refresh: refreshMileage } = useMileage();
  const [rates, setRates] = useState({});
  const [data, setData] = useState({ trips: [], total_miles: 0, total_deduction: 0 });
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), miles: "", purpose: "business", description: "", tolls: "" });
  const [preview, setPreview] = useState(0);
  // Inline edit / delete state
  const [editingTripId, setEditingTripId] = useState(null);
  const [editDraft, setEditDraft] = useState({ miles: "", description: "", tolls: "", purpose: "business" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () => api.get("/mileage").then((r) => setData(r.data)).catch(() => setData({ trips: [], total_miles: 0, total_deduction: 0 }));
  useEffect(() => {
    api.get("/mileage/rates").then((r) => setRates(r.data.rates || {})).catch(() => setRates({ business: 0.70, medical: 0.22, charity: 0.14, moving: 0.22 }));
    load();
  }, []);

  useEffect(() => {
    const rate = rates[form.purpose] || 0;
    const toll = Math.max(Number(form.tolls || 0), 0);
    setPreview(Number(form.miles || 0) * rate + toll);
  }, [form.miles, form.purpose, form.tolls, rates]);

  const submit = async () => {
    if (!form.miles) return toast.error("Enter miles");
    try {
      await api.post("/mileage", { ...form, miles: Number(form.miles), tolls: Number(form.tolls || 0) });
      toast.success("Trip logged");
      nudge(Number(form.miles), preview); // instant dashboard sync
      setForm({ ...form, miles: "", description: "", tolls: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log trip"); }
  };

  // ── Inline edit handlers ──────────────────────────────────────────────────
  const startEditTrip = (t) => {
    setEditingTripId(t.id);
    setEditDraft({ miles: t.miles, description: t.description || "", tolls: t.tolls || 0, purpose: t.purpose });
    setConfirmDeleteId(null);
  };

  const saveEditTrip = async (t) => {
    const newMiles  = Math.max(0, Number(editDraft.miles)  || t.miles);
    const newTolls  = Math.max(0, Number(editDraft.tolls)  || 0);
    const newDed    = newMiles * (t.rate || 0) + newTolls;
    const dMiles    = newMiles - t.miles;
    const dDed      = newDed - t.deduction;
    try {
      await api.patch(`/mileage/${t.id}`, {
        miles: newMiles, tolls: newTolls,
        purpose: editDraft.purpose, description: editDraft.description,
      });
    } catch { toast.error("Could not persist edit"); return; }
    setData((d) => ({
      ...d,
      trips: d.trips.map((tr) =>
        tr.id === t.id
          ? { ...tr, miles: newMiles, description: editDraft.description, tolls: newTolls, deduction: newDed, purpose: editDraft.purpose }
          : tr
      ),
      total_miles: Math.max(0, d.total_miles + dMiles),
      total_deduction: Math.max(0, d.total_deduction + dDed),
    }));
    nudge(dMiles, dDed); // instant dashboard sync
    setEditingTripId(null);
    toast.success("Trip updated");
  };

  // ── Delete with confirm ───────────────────────────────────────────────────
  const confirmDelete = async (t) => {
    try {
      await api.delete(`/mileage/${t.id}`);
      setData((d) => ({
        ...d,
        trips: d.trips.filter((tr) => tr.id !== t.id),
        total_miles: Math.max(0, d.total_miles - t.miles),
        total_deduction: Math.max(0, d.total_deduction - t.deduction),
      }));
      nudge(-t.miles, -t.deduction); // instant dashboard sync
      setConfirmDeleteId(null);
      toast.success("Trip removed");
    } catch { toast.error("Could not delete trip"); }
  };

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5"><div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Total Miles</div><div className="text-3xl font-mono font-bold text-white mt-2">{data.total_miles.toFixed(1)}</div></div>
        <div className="glass rounded-2xl p-5"><div className="text-slate-400 text-xs uppercase tracking-[0.2em]">IRS Deduction</div><div className="text-3xl font-mono font-bold text-emerald-400 mt-2">${data.total_deduction.toFixed(2)}</div></div>
        <div className="glass rounded-2xl p-5"><div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Business Rate 2026</div><div className="text-3xl font-mono font-bold text-white mt-2">${(rates.business || 0).toFixed(2)}<span className="text-slate-500 text-base">/mi</span></div></div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5"><Car size={18} className="text-emerald-400" /><h3 className="font-semibold text-white tracking-tight">Log a trip</h3></div>
        <div className="grid sm:grid-cols-6 gap-3">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="trip-date" className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white font-mono" />
          <input type="number" step="0.1" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} placeholder="Miles" data-testid="trip-miles" className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white font-mono outline-none focus:border-emerald-500/50" />
          <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="trip-purpose" className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white">
            {Object.keys(rates).map((k) => <option key={k} value={k}>{k} · ${rates[k]?.toFixed(2)}/mi</option>)}
          </select>
          <input type="number" step="0.01" min="0" value={form.tolls} onChange={(e) => setForm({ ...form, tolls: e.target.value })} placeholder="Tolls $" data-testid="trip-tolls" className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white font-mono outline-none focus:border-emerald-500/50" />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Purpose (e.g. Client visit)" data-testid="trip-desc" className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white outline-none focus:border-emerald-500/50" />
          <button onClick={submit} data-testid="trip-log-btn" className="btn-neon h-11 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center justify-center gap-2">
            Log <span className="font-mono">${preview.toFixed(2)}</span>
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <h3 className="font-semibold text-white tracking-tight">Trip log</h3>
          <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">{data.trips.length} trips</span>
        </div>
        {data.trips.length === 0 ? (
          <EnterpriseEmptyState label="AWAITING TELEMETRY · NO TRIPS RECORDED IN SYSTEM" testid="mileage-empty-scanner" />
        ) : (
          <div className="divide-y divide-white/5">
            {data.trips.map((t) => {
              const isEditingThis = editingTripId === t.id;
              const isConfirmingThis = confirmDeleteId === t.id;
              return (
                <div key={t.id} data-testid={`trip-${t.id}`} className="px-6 py-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-300 shrink-0 mt-0.5"><Car size={14} /></div>

                  {/* Main content — static or edit inputs */}
                  <div className="flex-1 min-w-0">
                    {isEditingThis ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <input
                            type="number" step="0.1" min="0"
                            value={editDraft.miles}
                            onChange={(e) => setEditDraft((d) => ({ ...d, miles: e.target.value }))}
                            placeholder="Miles"
                            data-testid={`edit-trip-miles-${t.id}`}
                            className="h-8 px-2 rounded bg-white/5 border border-white/15 text-white text-xs font-mono focus:outline-none focus:border-emerald-500/50"
                          />
                          <input
                            type="number" step="0.01" min="0"
                            value={editDraft.tolls}
                            onChange={(e) => setEditDraft((d) => ({ ...d, tolls: e.target.value }))}
                            placeholder="Tolls $"
                            data-testid={`edit-trip-tolls-${t.id}`}
                            className="h-8 px-2 rounded bg-white/5 border border-white/15 text-white text-xs font-mono focus:outline-none focus:border-emerald-500/50"
                          />
                          <select
                            value={editDraft.purpose}
                            onChange={(e) => setEditDraft((d) => ({ ...d, purpose: e.target.value }))}
                            data-testid={`edit-trip-purpose-${t.id}`}
                            className="h-8 px-2 rounded bg-slate-900 border border-white/15 text-white text-xs focus:outline-none focus:border-emerald-500/50"
                          >
                            {Object.keys(rates).map((k) => <option key={k} value={k}>{k}</option>)}
                          </select>
                          <input
                            value={editDraft.description}
                            onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
                            placeholder="Description"
                            data-testid={`edit-trip-desc-${t.id}`}
                            className="h-8 px-2 rounded bg-white/5 border border-white/15 text-white text-xs focus:outline-none focus:border-emerald-500/50"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveEditTrip(t)}
                            data-testid={`save-trip-${t.id}`}
                            className="h-7 px-3 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[9px] font-mono uppercase tracking-widest hover:bg-emerald-500/30 transition-colors"
                          >💾 Save</button>
                          <button
                            onClick={() => setEditingTripId(null)}
                            data-testid={`cancel-trip-edit-${t.id}`}
                            className="h-7 px-3 rounded bg-white/5 border border-white/10 text-slate-400 text-[9px] font-mono hover:bg-white/10 transition-colors"
                          >✗ Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-white truncate">{t.description || "Trip"}</div>
                        <div className="text-xs text-slate-500 font-mono">{t.date} · {t.purpose} · ${t.rate}/mi{t.tolls ? ` · +$${Number(t.tolls).toFixed(2)} toll` : ""}</div>
                      </>
                    )}
                  </div>

                  {/* Miles + deduction (hidden during edit) */}
                  {!isEditingThis && (
                    <div className="text-right shrink-0">
                      <div className="font-mono text-white">{t.miles.toFixed(1)} mi</div>
                      <div className="font-mono font-bold text-emerald-300">${t.deduction.toFixed(2)}</div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 shrink-0 mt-0.5">
                    {isConfirmingThis ? (
                      <div className="space-y-1 text-right">
                        <div className="text-[9px] font-mono uppercase tracking-widest text-red-300 leading-tight whitespace-nowrap">Delete Log Entry?</div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => confirmDelete(t)}
                            data-testid={`confirm-delete-trip-${t.id}`}
                            className="h-5 px-2 rounded bg-red-500/20 border border-red-500/40 text-red-300 text-[9px] font-mono hover:bg-red-500/30 transition-colors"
                          >✓ Yes</button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            data-testid={`cancel-delete-trip-${t.id}`}
                            className="h-5 px-2 rounded bg-white/5 border border-white/10 text-slate-400 text-[9px] font-mono hover:bg-white/10 transition-colors"
                          >✗</button>
                        </div>
                      </div>
                    ) : isEditingThis ? null : (
                      <>
                        <button
                          onClick={() => startEditTrip(t)}
                          data-testid={`edit-trip-${t.id}`}
                          className="h-7 px-2 rounded bg-white/[0.04] border border-white/10 text-slate-300 text-[9px] font-mono hover:bg-cyan-500/10 hover:text-cyan-300 hover:border-cyan-500/30 transition-colors"
                        >✏️</button>
                        <button
                          onClick={() => { setConfirmDeleteId(t.id); setEditingTripId(null); }}
                          data-testid={`delete-trip-${t.id}`}
                          className="h-7 px-2 rounded bg-white/[0.04] border border-white/10 text-slate-400 text-[9px] font-mono hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30 transition-colors"
                        >❌</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Advance Signals (admin) ----------------
const RISK_PROFILES = ["LOW", "MODERATE", "HIGH", "CRITICAL"];
const ROUTING_NODES = ["Node Alpha", "Node Beta", "Node Gamma", "Node Delta", "Manual Review"];

function SignalsTab() {
  const [signals, setSignals] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [freezeAll, setFreezeAll] = useState(false);
  const [freezeLoading, setFreezeLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [releasingId, setReleasingId] = useState(null);
  const [releaseConfirming, setReleaseConfirming] = useState(false);
  const [fadingIds, setFadingIds] = useState(new Set());

  const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const loadAll = () => {
    api.get("/admin/capital-advance/metrics")
      .then((r) => {
        const d = r.data || {};
        setMetrics(d);
        setSignals(d.recent || []);
      })
      .catch(() => { setMetrics({}); setSignals([]); });
    api.get("/v1/admin/signals/freeze-state")
      .then((r) => setFreezeAll(!!r.data?.frozen))
      .catch(() => {});
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (s) => {
    setEditingId(s.receipt_id);
    setEditDraft({
      email: s.email || "",
      advance_amount: String(s.advance_amount || ""),
      risk_profile: s.risk_profile || "MODERATE",
      routing_node: s.routing_node || "Node Alpha",
      factor_fee: String(s.factor_fee != null ? s.factor_fee : "1.18"),
    });
    setReleasingId(null);
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = async (receiptId) => {
    setSavingId(receiptId);
    try {
      const payload = {
        receipt_id: receiptId,
        email: editDraft.email || undefined,
        advance_amount: editDraft.advance_amount ? parseFloat(editDraft.advance_amount) : undefined,
        risk_profile: editDraft.risk_profile || undefined,
        routing_node: editDraft.routing_node || undefined,
        factor_fee: editDraft.factor_fee ? parseFloat(editDraft.factor_fee) : undefined,
      };
      const { data } = await api.patch("/v1/admin/signals/update", payload);
      setSignals((prev) => prev.map((s) => s.receipt_id === receiptId ? { ...s, ...data } : s));
      setEditingId(null);
      setEditDraft({});
      toast.success("Signal updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const openRelease = (s) => {
    if (freezeAll) { toast.error("Disbursements frozen — lift the shield first"); return; }
    setReleasingId(s.receipt_id);
    setReleaseConfirming(false);
    setEditingId(null);
  };

  const confirmRelease = async (receiptId) => {
    setReleaseConfirming(true);
    try {
      const { data } = await api.patch("/v1/admin/signals/update", { receipt_id: receiptId, status: "released" });
      setSignals((prev) => prev.map((s) => s.receipt_id === receiptId ? { ...s, ...data, status: "released" } : s));
      toast.success("Advance signal released for disbursement");
      setReleasingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    } finally {
      setReleaseConfirming(false);
    }
  };

  const deleteSignal = async (receiptId) => {
    setFadingIds((prev) => new Set([...prev, receiptId]));
    setTimeout(async () => {
      try {
        await api.delete(`/v1/admin/signals/remove?receipt_id=${encodeURIComponent(receiptId)}`);
        setSignals((prev) => prev.filter((s) => s.receipt_id !== receiptId));
        setFadingIds((prev) => { const n = new Set(prev); n.delete(receiptId); return n; });
        toast.success("Signal removed");
      } catch (e) {
        setFadingIds((prev) => { const n = new Set(prev); n.delete(receiptId); return n; });
        toast.error(e?.response?.data?.detail || "Delete failed");
      }
    }, 320);
  };

  const toggleFreezeAll = async () => {
    setFreezeLoading(true);
    try {
      const { data } = await api.post("/v1/admin/signals/freeze-toggle");
      setFreezeAll(data.frozen);
      toast[data.frozen ? "warning" : "success"](
        data.frozen ? "All advance disbursements FROZEN" : "Disbursements shield lifted · pipeline active"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Freeze toggle failed");
    } finally {
      setFreezeLoading(false);
    }
  };

  const riskColor = (rp) => {
    const m = { LOW: "#34d399", MODERATE: "#f59e0b", HIGH: "#f87171", CRITICAL: "#ef4444" };
    return m[String(rp).toUpperCase()] || "#94a3b8";
  };
  const statusChip = (status) => {
    const cfg = {
      pending:  { label: "PENDING",  bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.4)",  color: "#fbbf24" },
      released: { label: "RELEASED", bg: "rgba(52,211,153,0.15)", border: "rgba(52,211,153,0.4)", color: "#34d399" },
      frozen:   { label: "FROZEN",   bg: "rgba(248,113,113,0.15)", border: "rgba(248,113,113,0.4)", color: "#f87171" },
    };
    return cfg[String(status).toLowerCase()] || cfg.pending;
  };

  if (signals === null) return <div className="text-slate-500 text-sm font-mono uppercase tracking-widest text-center py-16">Loading signals…</div>;

  return (
    <div className="space-y-6" data-testid="signals-tab">
      {/* ── KPI strip ─────────────────────────────────────── */}
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-5" data-testid="signal-stat-users">
          <div className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2"><Users size={11} /> Interested Users</div>
          <div className="text-3xl font-mono font-bold text-white mt-2">{metrics.unique_users || 0}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">{metrics.total_signals || 0} total signals</div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="signal-stat-requested">
          <div className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2"><DollarSign size={11} /> Total $ Requested</div>
          <div className="text-3xl font-mono font-bold text-emerald-400 mt-2">{fmt(metrics.total_requested_dollar)}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">{metrics.receipts_flagged || 0} receipts flagged</div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="signal-stat-avg">
          <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Avg Advance</div>
          <div className="text-3xl font-mono font-bold text-white mt-2">{fmt(metrics.avg_advance)}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">per request</div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="signal-stat-receipt-value">
          <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Underlying Value</div>
          <div className="text-3xl font-mono font-bold text-white mt-2">{fmt(metrics.total_receipt_value)}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">receipts collateral</div>
        </div>
      </div>

      {/* ── Global Freeze Shield ──────────────────────────── */}
      <div
        data-testid="signals-freeze-shield"
        className="glass rounded-2xl px-5 py-4 flex items-center justify-between gap-4"
        style={{
          border: freezeAll ? "1px solid rgba(248,113,113,0.55)" : "1px solid rgba(6,182,212,0.22)",
          background: freezeAll ? "rgba(248,113,113,0.07)" : "rgba(6,182,212,0.04)",
          transition: "border-color 260ms ease, background 260ms ease",
        }}
      >
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.22em]" style={{ color: freezeAll ? "#f87171" : "#94a3b8" }}>
            {freezeAll ? "DISBURSEMENTS SHIELD ACTIVE · ALL ADVANCES FROZEN" : "DISBURSEMENT PIPELINE · OPERATIONAL"}
          </div>
          <div className="text-[10px] font-mono text-slate-600 mt-0.5">
            Toggle freezes every pending + released signal · no new disbursements will be routed
          </div>
        </div>
        <button
          type="button"
          onClick={toggleFreezeAll}
          disabled={freezeLoading}
          data-testid="signals-freeze-toggle-btn"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
          style={{
            background: freezeAll ? "rgba(248,113,113,0.18)" : "rgba(6,182,212,0.10)",
            border: freezeAll ? "1px solid rgba(248,113,113,0.55)" : "1px solid rgba(6,182,212,0.35)",
            color: freezeAll ? "#f87171" : "#06b6d4",
            boxShadow: freezeAll ? "0 0 16px -4px rgba(248,113,113,0.5)" : "none",
            opacity: freezeLoading ? 0.6 : 1,
          }}
        >
          {freezeAll ? <><Snowflake size={12} /> Lift Shield</> : <><Lock size={12} /> Freeze All</>}
        </button>
      </div>

      {/* ── Signal pipeline grid ─────────────────────────── */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <h3 className="font-semibold text-white tracking-tight flex items-center gap-2">
            <Zap size={14} className="text-emerald-400" /> Underwriting Pipeline
          </h3>
          <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">{signals.length} signals</span>
        </div>

        {signals.length === 0 ? (
          <EnterpriseEmptyState label="AWAITING TELEMETRY · SIGNALS" testid="signals-empty-scanner" />
        ) : (
          <div className="divide-y divide-white/5">
            {signals.map((s, i) => {
              const isFading = fadingIds.has(s.receipt_id);
              const isEditing = editingId === s.receipt_id;
              const isReleasing = releasingId === s.receipt_id;
              const isSaving = savingId === s.receipt_id;
              const sc = statusChip(s.status);
              const rc = riskColor(s.risk_profile || editDraft.risk_profile);
              return (
                <div
                  key={s.receipt_id}
                  data-testid={`signal-row-${i}`}
                  style={{
                    transition: "opacity 320ms ease, transform 320ms ease",
                    opacity: isFading ? 0 : 1,
                    transform: isFading ? "translateX(-14px)" : "none",
                  }}
                >
                  {/* ── Main row ─────────────────────────── */}
                  <div className="px-5 py-4 flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: `${rc}18`, border: `1px solid ${rc}40`, color: rc }}>
                      <Zap size={14} />
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0 space-y-3">
                      {isEditing ? (
                        /* ── Edit form ─────────────────── */
                        <div
                          className="space-y-3 rounded-xl p-4"
                          data-testid={`signal-edit-form-${i}`}
                          style={{ background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.18)", animation: "sigEditSlideIn 200ms ease both" }}
                        >
                          {/* Row 1: email + advance amount */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Email</label>
                              <input
                                type="email"
                                value={editDraft.email}
                                onChange={(e) => setEditDraft((d) => ({ ...d, email: e.target.value }))}
                                data-testid={`signal-edit-email-${i}`}
                                className="w-full rounded-lg px-3 py-2 text-xs font-mono text-white outline-none"
                                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(6,182,212,0.25)" }}
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Advance Amount ($)</label>
                              <input
                                type="number"
                                step="100"
                                value={editDraft.advance_amount}
                                onChange={(e) => setEditDraft((d) => ({ ...d, advance_amount: e.target.value }))}
                                data-testid={`signal-edit-amount-${i}`}
                                className="w-full rounded-lg px-3 py-2 text-xs font-mono text-white outline-none"
                                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(6,182,212,0.25)" }}
                              />
                            </div>
                          </div>
                          {/* Row 2: risk profile + routing node */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Risk Profile</label>
                              <select
                                value={editDraft.risk_profile}
                                onChange={(e) => setEditDraft((d) => ({ ...d, risk_profile: e.target.value }))}
                                data-testid={`signal-edit-risk-${i}`}
                                className="w-full rounded-lg px-3 py-2 text-xs font-mono text-white outline-none appearance-none"
                                style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${riskColor(editDraft.risk_profile)}40` }}
                              >
                                {RISK_PROFILES.map((r) => <option key={r} value={r}>{r}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Routing Node</label>
                              <select
                                value={editDraft.routing_node}
                                onChange={(e) => setEditDraft((d) => ({ ...d, routing_node: e.target.value }))}
                                data-testid={`signal-edit-node-${i}`}
                                className="w-full rounded-lg px-3 py-2 text-xs font-mono text-white outline-none appearance-none"
                                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(6,182,212,0.25)" }}
                              >
                                {ROUTING_NODES.map((n) => <option key={n} value={n}>{n}</option>)}
                              </select>
                            </div>
                          </div>
                          {/* Row 3: factor fee slider */}
                          <div>
                            <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500 block mb-1">
                              Factor Fee · <span className="text-cyan-400">{parseFloat(editDraft.factor_fee || 1.18).toFixed(2)}×</span>
                            </label>
                            <input
                              type="range" min="1.05" max="1.50" step="0.01"
                              value={editDraft.factor_fee}
                              onChange={(e) => setEditDraft((d) => ({ ...d, factor_fee: e.target.value }))}
                              data-testid={`signal-edit-factor-${i}`}
                              className="w-full h-1 appearance-none rounded-full outline-none"
                              style={{ accentColor: "#06b6d4" }}
                            />
                            <div className="flex justify-between text-[8px] font-mono text-slate-600 mt-0.5"><span>1.05×</span><span>1.50×</span></div>
                          </div>
                          {/* Save / Cancel */}
                          <div className="flex gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => saveEdit(s.receipt_id)}
                              disabled={isSaving}
                              data-testid={`signal-save-btn-${i}`}
                              className="px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-opacity"
                              style={{ background: "rgba(6,182,212,0.18)", border: "1px solid rgba(6,182,212,0.45)", color: "#06b6d4", opacity: isSaving ? 0.6 : 1 }}
                            >
                              {isSaving ? "Saving…" : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              data-testid={`signal-cancel-btn-${i}`}
                              className="px-4 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
                              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ── Read view ─────────────────── */
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                          <span className="text-white text-sm font-medium truncate max-w-[180px]" data-testid={`signal-email-${i}`}>{s.email || "anonymous"}</span>
                          <span className="text-xs font-mono text-slate-500">receipt …{String(s.receipt_id).slice(-8)}</span>
                          <span className="text-xs font-mono text-slate-500">{s.signal_count || 1}× signals</span>
                          {/* Risk badge */}
                          {s.risk_profile && (
                            <span
                              data-testid={`signal-risk-badge-${i}`}
                              className="text-[9px] font-bold font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                              style={{ background: `${riskColor(s.risk_profile)}18`, border: `1px solid ${riskColor(s.risk_profile)}50`, color: riskColor(s.risk_profile) }}
                            >
                              {s.risk_profile}
                            </span>
                          )}
                          {/* Routing node */}
                          {s.routing_node && (
                            <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest" data-testid={`signal-node-${i}`}>
                              via {s.routing_node}
                            </span>
                          )}
                          {/* Status chip */}
                          <span
                            data-testid={`signal-status-${i}`}
                            className="text-[9px] font-bold font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                            style={{ background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color }}
                          >
                            {sc.label}
                          </span>
                        </div>
                      )}

                      {/* ── Release popover ────────────── */}
                      {isReleasing && !isEditing && (
                        <div
                          data-testid={`signal-release-popover-${i}`}
                          className="rounded-xl px-4 py-3 space-y-2"
                          style={{ background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.3)", animation: "sigReleaseIn 180ms ease both" }}
                        >
                          <div className="text-xs font-mono text-emerald-300 uppercase tracking-widest">Confirm Disbursement Release</div>
                          <div className="text-[10px] text-slate-400 font-mono">
                            Route {fmt(s.advance_amount)} advance to {s.email || "applicant"} via {s.routing_node || "default node"} · factor {s.factor_fee != null ? `${Number(s.factor_fee).toFixed(2)}×` : "–"}
                          </div>
                          <div className="flex gap-2 pt-0.5">
                            <button
                              type="button"
                              onClick={() => confirmRelease(s.receipt_id)}
                              disabled={releaseConfirming}
                              data-testid={`signal-release-confirm-${i}`}
                              className="px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-opacity"
                              style={{ background: "rgba(52,211,153,0.18)", border: "1px solid rgba(52,211,153,0.5)", color: "#34d399", opacity: releaseConfirming ? 0.6 : 1 }}
                            >
                              {releaseConfirming ? "Routing…" : "Confirm Release"}
                            </button>
                            <button
                              type="button"
                              onClick={() => setReleasingId(null)}
                              data-testid={`signal-release-cancel-${i}`}
                              className="px-4 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
                              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Amount column */}
                    {!isEditing && (
                      <div className="text-right shrink-0">
                        <div className="font-mono text-white text-sm">{fmt(s.receipt_total)}</div>
                        <div className="font-mono font-bold text-emerald-300 text-xs">{fmt(s.advance_amount)} adv</div>
                        {s.factor_fee != null && (
                          <div className="text-[9px] font-mono text-slate-500">{Number(s.factor_fee).toFixed(2)}× fee</div>
                        )}
                      </div>
                    )}

                    {/* Action buttons */}
                    {!isEditing && !isReleasing && (
                      <div className="shrink-0 flex flex-col gap-1.5">
                        <button
                          type="button"
                          onClick={() => startEdit(s)}
                          data-testid={`signal-edit-btn-${i}`}
                          title="Edit signal metrics"
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-cyan-300 transition-colors"
                          style={{ background: "rgba(6,182,212,0.07)", border: "1px solid rgba(6,182,212,0.18)" }}
                        >
                          <TrendingUp size={11} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openRelease(s)}
                          data-testid={`signal-release-btn-${i}`}
                          title="Release for disbursement"
                          disabled={freezeAll || s.status === "released"}
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                          style={{
                            background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.22)",
                            color: freezeAll || s.status === "released" ? "#334155" : "#34d399",
                            cursor: freezeAll || s.status === "released" ? "not-allowed" : "pointer",
                          }}
                        >
                          <ArrowUpRight size={11} />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteSignal(s.receipt_id)}
                          data-testid={`signal-delete-btn-${i}`}
                          title="Remove signal"
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 transition-colors"
                          style={{ background: "rgba(248,113,113,0.05)", border: "1px solid rgba(248,113,113,0.12)" }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------- CPA Lead Marketplace Pipeline Engine ----------------
const _DEMO_CPA_LEADS = [
  { lead_id: "cpa-lead-001", name: "Jordan Blake", email: "j.b***@corp.io", subscription_tier: "enterprise_fleet", risk_score: 87, risk_label: "High", lead_count: 4, status: "new", tokenized: false, dispatched: false, purged: false, notes: "Multiple Schedule C inquiries · 3 active LLCs · sole proprietor", last_interest_at: "2026-02-19T10:15:00Z" },
  { lead_id: "cpa-lead-002", name: "Avery Marsh", email: "a.m***@ventures.io", subscription_tier: "premium_shield", risk_score: 74, risk_label: "High", lead_count: 2, status: "tokenized", tokenized: true, dispatched: false, purged: false, notes: "Crypto trading gains · W-2 crossover · backdoor Roth candidate", last_interest_at: "2026-02-18T16:42:00Z" },
  { lead_id: "cpa-lead-003", name: "Rhea Okonkwo", email: "r.ok***@freelance.io", subscription_tier: "premium_shield", risk_score: 58, risk_label: "Moderate", lead_count: 1, status: "new", tokenized: false, dispatched: false, purged: false, notes: "First-year independent contractor · home office deduction flagged", last_interest_at: "2026-02-17T22:10:00Z" },
  { lead_id: "cpa-lead-004", name: "Marcus Vance", email: "m.v***@fleet.io", subscription_tier: "enterprise_fleet", risk_score: 91, risk_label: "High", lead_count: 6, status: "dispatched", tokenized: true, dispatched: true, purged: false, notes: "Fleet vehicle depreciation audit · §179 over-election risk", last_interest_at: "2026-02-16T08:55:00Z" },
  { lead_id: "cpa-lead-005", name: "Sierra Novak", email: "s.n***@agency.io", subscription_tier: "premium_shield", risk_score: 63, risk_label: "Moderate", lead_count: 2, status: "new", tokenized: false, dispatched: false, purged: false, notes: "Digital agency owner · QBI deduction eligibility · mileage crossover", last_interest_at: "2026-02-15T19:00:00Z" },
];

function CpaLeadsTab() {
  const [leads, setLeads] = useState(_DEMO_CPA_LEADS);
  const [unlockLead, setUnlockLead] = useState(null);
  const [unlockStep, setUnlockStep] = useState("idle"); // idle | processing | complete
  const [purgeConfirm, setPurgeConfirm] = useState(null); // lead_id pending purge confirm
  const [minScore, setMinScore] = useState(0);
  const [sparkRange, setSparkRange] = useState("7W");
  const [alertThreshold, setAlertThreshold] = useState(80);
  const alertedIdsRef = useRef(new Set());
  const csvInputRef = useRef(null);

  useEffect(() => {
    api.get("/v1/cpa-leads")
      .then((r) => { if (r.data.leads && r.data.leads.length > 0) setLeads(r.data.leads); })
      .catch(() => {});
  }, []);

  // ── Score Threshold Alert — fires once per lead per threshold value ──
  useEffect(() => {
    if (!alertThreshold) return;
    leads.filter((l) => !l.purged).forEach((lead) => {
      const key = `${lead.lead_id}:${alertThreshold}`;
      if ((lead.risk_score || 0) >= alertThreshold && !alertedIdsRef.current.has(key)) {
        alertedIdsRef.current.add(key);
        toast.warning(`Score alert: ${lead.name} · ${lead.risk_score} ≥ ${alertThreshold}`, {
          id: `score-alert-${lead.lead_id}`,
          duration: 4000,
        });
      }
    });
  }, [leads, alertThreshold]);

  const activeLeads = leads.filter((l) => !l.purged);
  const filteredLeads = minScore > 0 ? activeLeads.filter((l) => (l.risk_score || 0) >= minScore) : activeLeads;
  const dispatchedCount = activeLeads.filter((l) => l.dispatched || l.status === "transferred").length;
  const metrics = {
    total: activeLeads.length,
    high: activeLeads.filter((l) => l.risk_label === "High").length,
    moderate: activeLeads.filter((l) => l.risk_label === "Moderate").length,
    avgScore: activeLeads.length > 0
      ? Math.round(activeLeads.reduce((s, l) => s + (l.risk_score || 0), 0) / activeLeads.length)
      : 0,
    dispatchRate: activeLeads.length > 0 ? Math.round((dispatchedCount / activeLeads.length) * 100) : 0,
  };

  // ── Dynamic sparkline buckets (respects sparkRange) ──
  const sparkBuckets = (() => {
    const now = new Date();
    const cfg = {
      "7W":  { count: 7,  days: 7,  fmt: (i) => `W${i + 1}` },
      "30D": { count: 6,  days: 5,  fmt: (i) => `D${i * 5 + 1}` },
      "90D": { count: 9,  days: 10, fmt: (i) => `W${i + 1}` },
    }[sparkRange] || { count: 7, days: 7, fmt: (i) => `W${i + 1}` };
    const totalDays = cfg.count * cfg.days;
    const buckets = Array.from({ length: cfg.count }, (_, i) => ({ label: cfg.fmt(i), leads: 0, dispatched: 0 }));
    activeLeads.forEach((lead) => {
      if (!lead.last_interest_at) return;
      const daysAgo = Math.floor((now - new Date(lead.last_interest_at)) / 86400000);
      if (daysAgo > totalDays) return;
      const bucket = Math.max(0, Math.min(cfg.count - 1, cfg.count - 1 - Math.floor(daysAgo / cfg.days)));
      buckets[bucket].leads++;
      if (lead.dispatched) buckets[bucket].dispatched++;
    });
    return buckets;
  })();

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteValue, setEditingNoteValue] = useState("");
  const [sendingReport, setSendingReport] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [presets, setPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cpa_filter_presets") || "[]"); } catch { return []; }
  });
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetNameInput, setPresetNameInput] = useState("");
  const [reportCadence, setReportCadence] = useState(() => localStorage.getItem("cpa_report_cadence") || "none");

  const saveNote = (leadId) => {
    setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, notes: editingNoteValue } : l));
    api.patch(`/v1/cpa-leads/${leadId}`, { notes: editingNoteValue }).catch(() => {});
    setEditingNoteId(null);
    toast.success("Notes updated");
  };

  const sendReport = async () => {
    setSendingReport(true);
    try {
      await api.post("/v1/cpa-leads/report", { leads: filteredLeads, score_filter: minScore });
      toast.success("Pipeline snapshot dispatched via Resend");
    } catch {
      toast.error("Failed to send report");
    } finally {
      setSendingReport(false);
    }
  };

  const overrideScore = (leadId, raw) => {
    const val = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
    setLeads((prev) => prev.map((l) => l.lead_id !== leadId ? l : {
      ...l, risk_score: val, risk_label: val >= 75 ? "High" : "Moderate",
    }));
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSelectAll = () => setSelectedIds((prev) =>
    prev.size === filteredLeads.length
      ? new Set()
      : new Set(filteredLeads.map((l) => l.lead_id))
  );

  const bulkTokenize = () => {
    if (!selectedIds.size) return;
    setLeads((prev) => prev.map((l) =>
      selectedIds.has(l.lead_id) && !l.tokenized ? { ...l, tokenized: true, status: "tokenized" } : l
    ));
    selectedIds.forEach((id) => api.patch(`/v1/cpa-leads/${id}`, { tokenized: true, status: "tokenized" }).catch(() => {}));
    toast.success(`${selectedIds.size} lead${selectedIds.size > 1 ? "s" : ""} tokenized`);
    setSelectedIds(new Set());
  };

  const bulkDispatch = () => {
    if (!selectedIds.size) return;
    setLeads((prev) => prev.map((l) =>
      selectedIds.has(l.lead_id) && !l.dispatched ? { ...l, dispatched: true, status: "dispatched" } : l
    ));
    selectedIds.forEach((id) => api.patch(`/v1/cpa-leads/${id}`, { dispatched: true, status: "dispatched" }).catch(() => {}));
    toast.success(`${selectedIds.size} lead${selectedIds.size > 1 ? "s" : ""} dispatched · Resend payloads fired`);
    setSelectedIds(new Set());
  };

  const savePreset = () => {
    const name = presetNameInput.trim();
    if (!name) return;
    const entry = { name, minScore, sparkRange };
    const updated = [...presets.filter((p) => p.name !== name), entry];
    setPresets(updated);
    localStorage.setItem("cpa_filter_presets", JSON.stringify(updated));
    setSavingPreset(false);
    setPresetNameInput("");
    toast.success(`Preset "${name}" saved`);
  };

  const removePreset = (name) => {
    const updated = presets.filter((p) => p.name !== name);
    setPresets(updated);
    localStorage.setItem("cpa_filter_presets", JSON.stringify(updated));
  };

  const exportCSV = () => {
    const cols = ["name", "email", "subscription_tier", "risk_score", "risk_label", "status", "lead_count", "notes", "last_interest_at"];
    const rows = filteredLeads.map((l) => cols.map((c) => JSON.stringify(l[c] ?? "")).join(","));
    const blob = new Blob([[cols.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob),
      download: `cpa-leads-${new Date().toISOString().slice(0, 10)}.csv`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`${filteredLeads.length} lead${filteredLeads.length !== 1 ? "s" : ""} exported`);
  };

  const handleCSVImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = (ev.target.result || "").split("\n").filter(Boolean);
      if (lines.length < 2) { toast.error("CSV must have a header row + data rows"); return; }
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/[^a-z_]/g, "_"));
      const newLeads = lines.slice(1).filter(Boolean).map((line, i) => {
        const vals = line.split(",").map((v) => v.trim().replace(/^["']|["']$/g, ""));
        const obj = Object.fromEntries(headers.map((h, idx) => [h, vals[idx] || ""]));
        const score = parseInt(obj.risk_score || obj.score || "55", 10) || 55;
        return {
          lead_id: `csv-${Date.now()}-${i}`,
          name: obj.name || obj.full_name || `Lead ${i + 1}`,
          email: obj.email || "",
          subscription_tier: obj.subscription_tier || obj.tier || "premium_shield",
          risk_score: score,
          risk_label: obj.risk_label || (score >= 75 ? "High" : "Moderate"),
          lead_count: parseInt(obj.lead_count || "1", 10) || 1,
          status: "new", tokenized: false, dispatched: false, purged: false,
          notes: obj.notes || "Imported via CSV",
          last_interest_at: new Date().toISOString(),
        };
      }).filter((l) => l.name || l.email);
      if (!newLeads.length) { toast.error("No valid lead rows found in CSV"); return; }
      setLeads((prev) => [...newLeads, ...prev]);
      toast.success(`${newLeads.length} lead${newLeads.length > 1 ? "s" : ""} imported from CSV`);
      e.target.value = "";
    };
    reader.readAsText(file);
  };

  const tokenizeLead = (leadId) => {
    setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, tokenized: true, status: "tokenized" } : l));
    api.patch(`/v1/cpa-leads/${leadId}`, { tokenized: true, status: "tokenized" }).catch(() => {});
    toast.success("Lead node tokenized · encrypted handshake sequence initiated");
  };

  const dispatchLead = (leadId) => {
    setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, dispatched: true, status: "dispatched" } : l));
    api.patch(`/v1/cpa-leads/${leadId}`, { dispatched: true, status: "dispatched" }).catch(() => {});
    toast.success("Dispatch payload transmitted · broker channel open · Resend fired");
  };

  const purgeLead = (leadId) => {
    setLeads((prev) => prev.filter((l) => l.lead_id !== leadId));
    setPurgeConfirm(null);
    api.patch(`/v1/cpa-leads/${leadId}`, { purged: true }).catch(() => {});
    toast.success("Lead node purged from pipeline");
  };

  const confirmUnlock = () => {
    setUnlockStep("processing");
    setTimeout(() => {
      setUnlockStep("complete");
      setLeads((prev) => prev.map((l) =>
        l.lead_id === unlockLead.lead_id ? { ...l, status: "transferred", dispatched: true } : l
      ));
      api.patch(`/v1/cpa-leads/${unlockLead.lead_id}`, { status: "transferred", dispatched: true }).catch(() => {});
      setTimeout(() => {
        toast.success("CPA buyer unlock complete · lead transferred to broker network");
        setUnlockLead(null);
        setUnlockStep("idle");
      }, 1600);
    }, 1900);
  };

  const statusChip = (lead) => {
    if (lead.status === "transferred") return { label: "Dispatched & Transferred", color: "#10b981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)" };
    if (lead.dispatched) return { label: "Dispatched", color: "#06b6d4", bg: "rgba(6,182,212,0.10)", border: "rgba(6,182,212,0.25)" };
    if (lead.tokenized) return { label: "Tokenized", color: "#8b5cf6", bg: "rgba(139,92,246,0.10)", border: "rgba(139,92,246,0.25)" };
    if (lead.risk_label === "High") return { label: "High-Risk", color: "#f59e0b", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.25)" };
    return { label: lead.risk_label || "New", color: "#94a3b8", bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.15)" };
  };

  return (
    <div className="space-y-6" data-testid="cpa-leads-tab">

      {/* ── Pipeline Analytics Sparkline ── */}
      {(() => {
        const maxVal = Math.max(1, ...sparkBuckets.map((w) => w.leads));
        const W = 420, H = 64, padX = 8, padY = 8;
        const xStep = sparkBuckets.length > 1 ? (W - padX * 2) / (sparkBuckets.length - 1) : W / 2;
        const yScale = (v) => H - padY - (v / maxVal) * (H - padY * 2);
        const toPath = (key) =>
          sparkBuckets.map((w, i) => `${i === 0 ? "M" : "L"}${padX + i * xStep},${yScale(w[key])}`).join(" ");
        return (
          <div className="glass rounded-2xl px-5 pt-4 pb-3" data-testid="cpa-sparkline-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400">
                Pipeline Analytics · {sparkRange === "7W" ? "7-Week" : sparkRange === "30D" ? "30-Day" : "90-Day"} Trend
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Range picker */}
                <div className="flex gap-1" data-testid="sparkline-range-picker">
                  {["7W", "30D", "90D"].map((r) => (
                    <button
                      key={r}
                      onClick={() => setSparkRange(r)}
                      data-testid={`sparkline-range-${r}`}
                      className="px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest border transition-all"
                      style={sparkRange === r
                        ? { color: "#06b6d4", background: "rgba(6,182,212,0.12)", borderColor: "rgba(6,182,212,0.4)" }
                        : { color: "#475569", background: "transparent", borderColor: "rgba(71,85,105,0.25)" }}
                    >{r}</button>
                  ))}
                </div>
                {/* Alert threshold */}
                <div className="flex items-center gap-1.5" data-testid="alert-threshold-control">
                  <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest">Alert ≥</span>
                  <input
                    type="number" min="0" max="100" step="5"
                    value={alertThreshold}
                    onChange={(e) => { alertedIdsRef.current.clear(); setAlertThreshold(Number(e.target.value)); }}
                    data-testid="alert-threshold-input"
                    className="w-12 text-center rounded px-1 py-0.5 text-[10px] font-mono border bg-transparent outline-none"
                    style={{ color: "#f59e0b", borderColor: "rgba(245,158,11,0.3)" }}
                  />
                </div>
                <span className="flex items-center gap-1.5 text-[9px] font-mono text-cyan-400"><span className="inline-block w-4 h-0.5 bg-cyan-400 rounded" />Volume</span>
                <span className="flex items-center gap-1.5 text-[9px] font-mono text-emerald-400"><span className="inline-block w-4 h-0.5 bg-emerald-400 rounded" />Dispatched</span>
              </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ overflow: "visible" }}>
              <defs>
                <linearGradient id="sparkLeadGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d={`${toPath("leads")} L${padX + (sparkBuckets.length - 1) * xStep},${H - padY} L${padX},${H - padY} Z`}
                fill="url(#sparkLeadGrad)"
              />
              <path d={toPath("leads")} fill="none" stroke="#06b6d4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(6,182,212,0.6))" }} />
              <path d={toPath("dispatched")} fill="none" stroke="#34d399" strokeWidth="1.5" strokeDasharray="3 3" strokeLinecap="round" strokeLinejoin="round" />
              {sparkBuckets.map((w, i) => (
                <g key={i}>
                  <circle cx={padX + i * xStep} cy={yScale(w.leads)} r="3" fill="#06b6d4" style={{ filter: "drop-shadow(0 0 3px rgba(6,182,212,0.8))" }} />
                  <text x={padX + i * xStep} y={H} textAnchor="middle" fontSize="7" fill="#475569" fontFamily="monospace">{w.label}</text>
                  {w.leads > 0 && (
                    <text x={padX + i * xStep} y={yScale(w.leads) - 5} textAnchor="middle" fontSize="7.5" fill="#94a3b8" fontFamily="monospace">{w.leads}</text>
                  )}
                </g>
              ))}
            </svg>
          </div>
        );
      })()}

      {/* ── Metrics Strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="glass rounded-2xl p-5" data-testid="lead-stat-total">
          <div className="text-slate-400 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 font-mono"><Users size={11} /> Total Leads</div>
          <div className="text-3xl font-mono font-bold text-white mt-2">{metrics.total}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">pipeline nodes</div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="lead-stat-high">
          <div className="text-slate-400 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 font-mono"><Flame size={11} /> High-Risk</div>
          <div className="text-3xl font-mono font-bold text-amber-300 mt-2">{metrics.high}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">priority targets</div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="lead-stat-moderate">
          <div className="text-slate-400 text-[10px] uppercase tracking-[0.2em] font-mono">Moderate</div>
          <div className="text-3xl font-mono font-bold text-white mt-2">{metrics.moderate}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">mid-value nodes</div>
        </div>
        <div className="glass rounded-2xl p-5" data-testid="lead-stat-avg-score">
          <div className="text-slate-400 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 font-mono"><TrendingUp size={11} /> Avg Score</div>
          <div className="text-3xl font-mono font-bold text-white mt-2">{metrics.avgScore}</div>
          <div className="text-slate-500 text-xs mt-1 font-mono">out of 100</div>
        </div>
        <div className="glass rounded-2xl p-5 relative overflow-hidden" data-testid="lead-stat-dispatch-rate">
          <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(135deg, rgba(16,185,129,${(metrics.dispatchRate / 100) * 0.12}) 0%, transparent 60%)` }} />
          <div className="relative text-slate-400 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 font-mono"><TrendingUp size={11} /> Dispatch Rate</div>
          <div className="relative text-3xl font-mono font-bold text-emerald-400 mt-2">{metrics.dispatchRate}%</div>
          <div className="relative text-slate-500 text-xs mt-1 font-mono">{dispatchedCount} / {metrics.total} converted</div>
        </div>
      </div>

      {/* ── Lead Pipeline Grid ── */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-white tracking-tight flex items-center gap-2">
              <input
                type="checkbox"
                checked={filteredLeads.length > 0 && selectedIds.size === filteredLeads.length}
                onChange={toggleSelectAll}
                data-testid="select-all-checkbox"
                className="w-3.5 h-3.5 rounded-sm cursor-pointer"
                style={{ accentColor: "#06b6d4" }}
              />
              <HeartHandshake size={14} className="text-emerald-400" /> CPA Marketplace Pipeline
            </h3>
            <div className="flex items-center gap-2">
              {/* CSV Import */}
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleCSVImport}
                data-testid="cpa-csv-file-input"
              />
              <button
                onClick={() => csvInputRef.current?.click()}
                data-testid="cpa-csv-import-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
                style={{ color: "#a78bfa", background: "rgba(139,92,246,0.07)", borderColor: "rgba(139,92,246,0.2)" }}
              >
                <Plus size={10} /> Import CSV
              </button>
              <button
                onClick={exportCSV}
                data-testid="cpa-csv-export-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
                style={{ color: "#34d399", background: "rgba(52,211,153,0.07)", borderColor: "rgba(52,211,153,0.2)" }}
              >
                <Download size={10} /> Export CSV
              </button>
              <button
                onClick={sendReport}
                disabled={sendingReport}
                data-testid="cpa-send-report-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: "#06b6d4", background: "rgba(6,182,212,0.07)", borderColor: "rgba(6,182,212,0.2)" }}
              >
                <Radio size={10} className={sendingReport ? "animate-spin" : ""} /> {sendingReport ? "Sending…" : "Send Report"}
              </button>
              <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{filteredLeads.length} node{filteredLeads.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
          {/* Score Filter */}
          <div className="flex items-center gap-3 flex-wrap" data-testid="score-filter-bar">
            <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-slate-500 shrink-0">Score Filter</span>
            <div className="flex gap-1.5 flex-wrap">
              {[0, 50, 70, 85, 90].map((v) => (
                <button
                  key={v}
                  onClick={() => setMinScore(v)}
                  data-testid={`score-filter-${v}`}
                  className="px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest border transition-all"
                  style={minScore === v
                    ? { color: "#06b6d4", background: "rgba(6,182,212,0.12)", borderColor: "rgba(6,182,212,0.4)", boxShadow: "0 0 8px rgba(6,182,212,0.2)" }
                    : { color: "#64748b", background: "transparent", borderColor: "rgba(100,116,139,0.2)" }
                  }
                >{v === 0 ? "All" : `${v}+`}</button>
              ))}
            </div>
            <div className="flex-1 min-w-[120px] flex items-center gap-2">
              <input
                type="range" min="0" max="100" step="5"
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
                data-testid="score-range-slider"
                className="w-full h-1 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: "#06b6d4" }}
              />
              <span className="text-[10px] font-mono text-cyan-400 shrink-0 w-8 text-right">{minScore > 0 ? `${minScore}+` : "–"}</span>
            </div>
          </div>

          {/* ── Persistent Filter Presets ── */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="preset-filter-bar">
            <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-slate-500 shrink-0">Presets</span>
            {presets.map((p) => (
              <button
                key={p.name}
                onClick={() => { setMinScore(p.minScore); setSparkRange(p.sparkRange || "7W"); }}
                className="group flex items-center gap-1.5 px-2 py-0.5 rounded text-[9px] font-mono border transition-all hover:scale-105"
                style={{ color: "#a78bfa", borderColor: "rgba(139,92,246,0.3)", background: "rgba(139,92,246,0.07)" }}
                data-testid={`preset-chip-${p.name}`}
              >
                {p.name}
                <span
                  onClick={(e) => { e.stopPropagation(); removePreset(p.name); }}
                  className="opacity-0 group-hover:opacity-60 hover:!opacity-100 text-[8px] text-rose-400 transition-opacity leading-none"
                >✕</span>
              </button>
            ))}
            {savingPreset ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  value={presetNameInput}
                  onChange={(e) => setPresetNameInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") savePreset(); if (e.key === "Escape") { setSavingPreset(false); setPresetNameInput(""); } }}
                  placeholder="Name this preset…"
                  data-testid="preset-name-input"
                  className="rounded px-2 py-0.5 text-[10px] font-mono outline-none w-32"
                  style={{ color: "#e2e8f0", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(139,92,246,0.4)" }}
                />
                <button onClick={savePreset} data-testid="preset-save-confirm" className="text-[9px] font-mono text-purple-400 hover:text-purple-300 transition-colors">Save</button>
                <button onClick={() => { setSavingPreset(false); setPresetNameInput(""); }} className="text-[9px] font-mono text-slate-500 hover:text-slate-300 transition-colors">✕</button>
              </div>
            ) : (
              <button
                onClick={() => setSavingPreset(true)}
                data-testid="save-preset-btn"
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono border transition-all hover:scale-105"
                style={{ color: "#a78bfa", borderColor: "rgba(139,92,246,0.22)", background: "rgba(139,92,246,0.04)" }}
              >💾 Save Filter Preset</button>
            )}
          </div>

          {/* ── Automated Report Cadence ── */}
          <div className="flex items-center gap-3 pt-1 border-t border-white/[0.04]">
            <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-slate-500 shrink-0">📅 Report Cadence</span>
            <select
              value={reportCadence}
              onChange={(e) => {
                setReportCadence(e.target.value);
                localStorage.setItem("cpa_report_cadence", e.target.value);
                const label = e.target.value === "daily" ? "Daily Pipeline Snapshots" : e.target.value === "weekly" ? "Weekly Digest Overviews" : "Disabled";
                toast.success(`Report cadence → ${label}`);
              }}
              data-testid="report-cadence-select"
              className="rounded-lg px-3 py-1.5 text-[10px] font-mono border outline-none cursor-pointer"
              style={{
                background: "rgba(6,182,212,0.05)",
                borderColor: reportCadence !== "none" ? "rgba(6,182,212,0.35)" : "rgba(100,116,139,0.2)",
                color: reportCadence !== "none" ? "#06b6d4" : "#64748b",
              }}
            >
              <option value="none" style={{ background: "#0f172a" }}>— Off · Manual only</option>
              <option value="daily" style={{ background: "#0f172a" }}>Daily Pipeline Snapshots</option>
              <option value="weekly" style={{ background: "#0f172a" }}>Weekly Digest Overviews</option>
            </select>
            {reportCadence !== "none" && (
              <span className="text-[8px] font-mono text-emerald-500 uppercase tracking-[0.2em]">
                {reportCadence === "daily" ? "Daily · Active" : "Weekly · Active"}
              </span>
            )}
          </div>
        </div>
        {/* ── Bulk Operations Bar (slides in when rows selected) ── */}
        {selectedIds.size > 0 && (
          <div
            className="px-5 py-3 border-b border-white/5 flex items-center gap-3 flex-wrap"
            data-testid="bulk-ops-bar"
            style={{ background: "rgba(6,182,212,0.06)", animation: "bulkBarIn 180ms ease-out both" }}
          >
            <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest">{selectedIds.size} selected</span>
            <button
              onClick={bulkTokenize}
              data-testid="bulk-tokenize-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
              style={{ color: "#a78bfa", background: "rgba(139,92,246,0.1)", borderColor: "rgba(139,92,246,0.3)" }}
            >⚡ Bulk Tokenize</button>
            <button
              onClick={bulkDispatch}
              data-testid="bulk-dispatch-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
              style={{ color: "#06b6d4", background: "rgba(6,182,212,0.1)", borderColor: "rgba(6,182,212,0.3)" }}
            >💼 Bulk Dispatch</button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto text-[9px] font-mono text-slate-500 hover:text-slate-300 transition-colors"
              data-testid="bulk-clear-btn"
            >Clear</button>
          </div>
        )}
        {filteredLeads.length === 0 ? (
          <EnterpriseEmptyState label={minScore > 0 ? `NO NODES ABOVE SCORE ${minScore}` : "PIPELINE EMPTY · NO ACTIVE NODES"} testid="cpa-leads-empty-scanner" />
        ) : (
          <div className="divide-y divide-white/5">
            {filteredLeads.map((lead, i) => {
              const chip = statusChip(lead);
              const isHighPriority = lead.risk_label === "High" && lead.subscription_tier === "enterprise_fleet";
              const isPurging = purgeConfirm === lead.lead_id;
              return (
                <div
                  key={lead.lead_id}
                  data-testid={`lead-row-${i}`}
                  className="px-5 py-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 hover:bg-white/[0.015] transition-colors"
                  style={isHighPriority ? { borderLeft: "2px solid rgba(245,158,11,0.4)" } : {}}
                >
                  {/* Row select checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedIds.has(lead.lead_id)}
                    onChange={() => toggleSelect(lead.lead_id)}
                    data-testid={`row-select-${i}`}
                    onClick={(e) => e.stopPropagation()}
                    className="w-3.5 h-3.5 rounded-sm cursor-pointer flex-shrink-0"
                    style={{ accentColor: "#06b6d4" }}
                  />
                  {/* Icon + identity */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center border flex-shrink-0 ${
                      isHighPriority ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                        : lead.dispatched ? "bg-cyan-500/10 border-cyan-500/25 text-cyan-400"
                        : "bg-white/5 border-white/10 text-slate-400"
                    }`}>
                      {isHighPriority ? <Star size={14} /> : <HeartHandshake size={14} />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-medium text-sm truncate flex items-center gap-2">
                        {lead.name || lead.email}
                        {isHighPriority && (
                          <span className="text-[8px] font-mono text-amber-300 uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/25">
                            PRIORITY
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono mt-0.5">{lead.email} · {lead.subscription_tier || "free"} · {lead.lead_count}× engagements</div>
                      {/* ── Inline Notes Editor ── */}
                      {editingNoteId === lead.lead_id ? (
                        <div className="flex items-center gap-1.5 mt-1">
                          <input
                            autoFocus
                            value={editingNoteValue}
                            onChange={(e) => setEditingNoteValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveNote(lead.lead_id); if (e.key === "Escape") setEditingNoteId(null); }}
                            data-testid={`notes-edit-input-${i}`}
                            className="flex-1 rounded px-2 py-0.5 text-[10px] font-mono text-slate-200 outline-none"
                            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(6,182,212,0.3)" }}
                          />
                          <button onClick={() => saveNote(lead.lead_id)} data-testid={`notes-save-${i}`} className="text-[9px] font-mono text-emerald-400 hover:text-emerald-300 transition-colors">Save</button>
                          <button onClick={() => setEditingNoteId(null)} className="text-[9px] font-mono text-slate-500 hover:text-slate-300 transition-colors">✕</button>
                        </div>
                      ) : (
                        <div
                          className="text-[9px] text-slate-600 font-mono mt-0.5 truncate cursor-pointer hover:text-slate-400 transition-colors group flex items-center gap-1.5"
                          onClick={() => { setEditingNoteId(lead.lead_id); setEditingNoteValue(lead.notes || ""); }}
                          title="Click to edit notes"
                          data-testid={`notes-display-${i}`}
                        >
                          {lead.notes || <span className="italic text-slate-700">+ add notes</span>}
                          <span className="opacity-0 group-hover:opacity-60 transition-opacity text-[8px]">✎</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Score + mini-spark + status */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex flex-col items-end gap-0.5">
                      {/* ── Inline Score Override Input ── */}
                      <input
                        key={`so-${lead.lead_id}-${lead.risk_score}`}
                        type="number" min="0" max="100"
                        defaultValue={lead.risk_score}
                        onBlur={(e) => overrideScore(lead.lead_id, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        data-testid={`score-override-${i}`}
                        title="Override score (Enter or blur to apply)"
                        className="w-12 text-right bg-transparent font-mono text-base font-bold leading-none outline-none border-b"
                        style={{
                          color: lead.risk_label === "High" ? "#fcd34d" : "#cbd5e1",
                          borderColor: "rgba(255,255,255,0.08)",
                          MozAppearance: "textfield",
                        }}
                      />
                      {/* ── Score Trend Mini-Spark ── */}
                      {(() => {
                        const base = lead.risk_score || 50;
                        const s0 = (lead.lead_id.charCodeAt(0) || 1);
                        const s1 = (lead.lead_id.charCodeAt(lead.lead_id.length - 1) || 1);
                        const pts = [0, 1, 2, 3, 4].map((i) =>
                          Math.max(10, Math.min(99, base + ((s0 * (i + 7) * 13 + s1 * i * 5 + i * 23) % 26) - 13 - (4 - i) * 0.6))
                        );
                        const mn = Math.min(...pts), mx = Math.max(...pts), rng = Math.max(1, mx - mn);
                        const sw = 44, sh = 14;
                        const px = (idx) => ((idx / 4) * sw).toFixed(1);
                        const py = (v) => (sh - ((v - mn) / rng) * (sh * 0.78) - 1).toFixed(1);
                        const d = pts.map((v, idx) => `${idx === 0 ? "M" : "L"}${px(idx)},${py(v)}`).join(" ");
                        const trend = pts[4] > pts[0] + 2 ? "#34d399" : pts[4] < pts[0] - 2 ? "#f87171" : "#94a3b8";
                        return (
                          <svg viewBox={`0 0 ${sw} ${sh}`} width={sw} height={sh} data-testid={`score-trend-${i}`} style={{ overflow: "visible" }}>
                            <path d={d} fill="none" stroke={trend} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 2px ${trend}99)` }} />
                            <circle cx={px(4)} cy={py(pts[4])} r="1.8" fill={trend} />
                          </svg>
                        );
                      })()}
                      <div className="text-[8px] font-mono text-slate-500 uppercase tracking-widest">score</div>
                    </div>
                    <div
                      className="px-2.5 py-1 rounded-lg text-[9px] font-mono uppercase tracking-widest border font-semibold"
                      style={{ color: chip.color, background: chip.bg, borderColor: chip.border }}
                    >
                      {chip.label}
                    </div>
                  </div>

                  {/* HR Controls */}
                  {isPurging ? (
                    <div className="flex items-center gap-2 flex-shrink-0" data-testid={`purge-confirm-${i}`}>
                      <span className="text-[10px] font-mono text-rose-300 uppercase tracking-widest">Confirm Purge?</span>
                      <button
                        onClick={() => purgeLead(lead.lead_id)}
                        data-testid={`purge-confirm-yes-${i}`}
                        className="px-2 py-1 rounded text-[10px] font-mono text-rose-300 bg-rose-500/10 border border-rose-500/25 hover:bg-rose-500/20 transition-colors"
                      >Yes</button>
                      <button
                        onClick={() => setPurgeConfirm(null)}
                        className="px-2 py-1 rounded text-[10px] font-mono text-slate-400 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                      >No</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {!lead.tokenized && (
                        <button
                          onClick={() => tokenizeLead(lead.lead_id)}
                          data-testid={`tokenize-lead-${i}`}
                          title="Tokenize Lead"
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
                          style={{ color: "#a78bfa", background: "rgba(139,92,246,0.08)", borderColor: "rgba(139,92,246,0.2)" }}
                        >
                          Tokenize
                        </button>
                      )}
                      {!lead.dispatched && (
                        <button
                          onClick={() => dispatchLead(lead.lead_id)}
                          data-testid={`dispatch-lead-${i}`}
                          title="Dispatch to Broker"
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
                          style={{ color: "#06b6d4", background: "rgba(6,182,212,0.08)", borderColor: "rgba(6,182,212,0.2)" }}
                        >
                          Dispatch
                        </button>
                      )}
                      {lead.tokenized && !lead.dispatched && lead.status !== "transferred" && (
                        <button
                          onClick={() => { setUnlockLead(lead); setUnlockStep("idle"); }}
                          data-testid={`unlock-lead-${i}`}
                          title="Buyer Unlock"
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
                          style={{ color: "#34d399", background: "rgba(16,185,129,0.08)", borderColor: "rgba(16,185,129,0.2)" }}
                        >
                          Unlock
                        </button>
                      )}
                      <button
                        onClick={() => setPurgeConfirm(lead.lead_id)}
                        data-testid={`purge-lead-${i}`}
                        title="Purge Node"
                        className="px-2 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border transition-all hover:scale-105"
                        style={{ color: "#f87171", background: "rgba(248,113,113,0.06)", borderColor: "rgba(248,113,113,0.18)" }}
                      >
                        Purge
                      </button>
                    </div>
                  )}
                  {/* ── Dispatch History Log ── */}
                  {(lead.dispatched || lead.status === "transferred") && (
                    <div
                      className="sm:w-full flex items-center gap-2 flex-wrap px-3 py-1.5 rounded-lg border"
                      data-testid={`dispatch-log-${i}`}
                      style={{ background: "rgba(6,182,212,0.04)", borderColor: "rgba(6,182,212,0.13)" }}
                    >
                      <span className="text-[8px] font-mono text-cyan-500 uppercase tracking-[0.25em]">Dispatch Log</span>
                      <span className="text-[9px] font-mono text-slate-500">
                        {lead.dispatched_at
                          ? new Date(lead.dispatched_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "Recently dispatched"}
                      </span>
                      <span className="text-[9px] font-mono text-slate-700">·</span>
                      <span className="text-[9px] font-mono text-slate-600">{lead.dispatched_by || "admin · receipt-sheets"}</span>
                      <span className="ml-auto text-[8px] font-mono text-emerald-500 uppercase tracking-[0.2em]">broker · confirmed</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Buyer Unlock Overlay ── */}
      {unlockLead && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(12px)" }}
          data-testid="buyer-unlock-overlay"
        >
          <div
            className="w-full max-w-md rounded-2xl p-8 border relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(6,8,20,0.97) 0%, rgba(15,23,42,0.97) 100%)",
              borderColor: "rgba(16,185,129,0.3)",
              boxShadow: "0 0 60px rgba(16,185,129,0.15), 0 24px 64px rgba(0,0,0,0.6)",
              animation: "unlockPanelIn 240ms cubic-bezier(.22,1,.36,1) both",
            }}
            data-testid="buyer-unlock-panel"
          >
            {/* Ambient glow */}
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.25), transparent 65%)", filter: "blur(20px)" }} />

            {unlockStep === "complete" ? (
              <div className="text-center py-4" data-testid="buyer-unlock-complete">
                <div className="text-5xl mb-4" style={{ animation: "unlockCheckPop 400ms cubic-bezier(.34,1.56,.64,1) both" }}>
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto">
                    <Check size={28} className="text-emerald-400" />
                  </div>
                </div>
                <div className="text-emerald-400 font-mono text-[10px] uppercase tracking-[0.3em] mb-2">Transfer Complete</div>
                <div className="text-white font-bold text-xl">Lead Dispatched &amp; Transferred</div>
                <div className="text-slate-400 text-sm mt-2 font-mono">Broker network notified · status updated</div>
              </div>
            ) : (
              <>
                <div className="relative mb-6">
                  <div className="text-emerald-400 font-mono text-[10px] uppercase tracking-[0.3em] mb-1">CPA Buyer Unlock · Marketplace Transfer</div>
                  <div className="text-white font-bold text-xl tracking-tight">Authorize Lead Transfer</div>
                  <div className="text-slate-400 text-sm mt-1.5">Securely transfer this lead to the CPA broker network</div>
                </div>

                {/* Lead summary */}
                <div className="rounded-xl border p-4 mb-6 space-y-2" style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-xs font-mono">Lead Node</span>
                    <span className="text-white text-sm font-semibold">{unlockLead.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-xs font-mono">Risk Score</span>
                    <span className="text-amber-300 text-sm font-mono font-bold">{unlockLead.risk_score} · {unlockLead.risk_label}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-xs font-mono">Tier</span>
                    <span className="text-cyan-300 text-xs font-mono">{unlockLead.subscription_tier}</span>
                  </div>
                </div>

                {/* Simulated Stripe-style payment row */}
                <div className="rounded-xl border p-4 mb-6" style={{ background: "rgba(16,185,129,0.04)", borderColor: "rgba(16,185,129,0.2)" }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-emerald-400 font-mono text-[10px] uppercase tracking-widest">Broker Access Fee</span>
                    <span className="text-white font-mono font-bold text-lg">$0.00</span>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">Internal transfer · no charge · admin authorization only</div>
                  {/* Fake card input for visual */}
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}>
                    <div className="w-8 h-5 rounded bg-slate-600/50 flex-shrink-0" />
                    <span className="text-slate-500 text-xs font-mono tracking-widest">•••• •••• •••• 4242</span>
                    <span className="text-slate-600 text-[10px] ml-auto font-mono">ADMIN</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => { setUnlockLead(null); setUnlockStep("idle"); }}
                    className="flex-1 py-3 rounded-xl border text-sm font-mono uppercase tracking-widest text-slate-400 hover:text-slate-200 transition-colors"
                    style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)" }}
                    data-testid="buyer-unlock-cancel"
                  >Cancel</button>
                  <button
                    onClick={confirmUnlock}
                    disabled={unlockStep === "processing"}
                    data-testid="buyer-unlock-confirm"
                    className="flex-1 py-3 rounded-xl text-sm font-mono uppercase tracking-widest font-semibold transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.9), rgba(6,182,212,0.8))", color: "#fff", boxShadow: "0 0 24px rgba(16,185,129,0.35)" }}
                  >
                    {unlockStep === "processing" ? "Transferring…" : "Confirm Transfer"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Seeded demo roster (pre-populates the team table on first mount) ----
const SEEDED_ROSTER = [
  {
    user_id: "seed-ceo-001",
    name: "Quentin (CEO)",
    display_role: "Super Admin",
    email: "quentin@receiptsheets.io",
    org_role: "manager",
    subscription_tier: "Premium Shield · Elite",
    subscription_status: "active",
    picture: null,
    is_self: false,
  },
  {
    user_id: "seed-mgr-002",
    name: "Marcus Vance",
    display_role: "Logistics / Fleet Manager",
    email: "marcus.vance@receiptsheets.io",
    org_role: "member",
    subscription_tier: "Enterprise Command",
    subscription_status: "active",
    picture: null,
    is_self: false,
  },
  {
    user_id: "seed-ops-003",
    name: "Sarah Jenkins",
    display_role: "Commercial Field Operator",
    email: "sarah.jenkins@receiptsheets.io",
    org_role: "member",
    subscription_tier: "Standard Driver",
    subscription_status: "pending_invite",
    picture: null,
    is_self: false,
  },
];

// ---------------- Team Directory (org-scoped invites) ----------------
function TeamDirectoryTab() {
  const { user } = useAuth();
  const [data, setData] = useState({ org_id: "", count: 0, members: [] });
  const [pending, setPending] = useState({ count: 0, invites: [] });
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPasted, setBulkPasted] = useState("");
  const [bulking, setBulking] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  // Local demo roster state — seeded on mount, extended by local invites
  const [localRoster, setLocalRoster] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLocalEmail, setInviteLocalEmail] = useState("");
  const [inviteLocalName, setInviteLocalName] = useState("");
  // HR inline edit / approve / deactivate
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", email: "", display_role: "" });
  const [confirmAction, setConfirmAction] = useState(null); // { type: "approve"|"deactivate", userId }
  const [removingId, setRemovingId] = useState(null);
  const [editTimestamps, setEditTimestamps] = useState({}); // userId → ms — drives "Last edited · Xm ago"
  // Bulk role assignment
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRole, setBulkRole] = useState("Standard Driver");
  // Role-change audit log
  const [auditLog, setAuditLog] = useState([]);
  const [auditOpen, setAuditOpen] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/org/members").catch(() => ({ data: { org_id: "", count: 0, members: [] } })),
      api.get("/org/invites").catch(() => ({ data: { count: 0, invites: [] } })),
      api.get("/org/invite/funnel?days=30").catch(() => ({ data: null })),
      api.get("/v1/team").catch(() => ({ data: [] })),
    ])
      .then(([m, p, f, t]) => {
        const apiData = m.data || { org_id: "", count: 0, members: [] };
        setData(apiData);
        const teamRows = Array.isArray(t.data) ? t.data : [];
        if (teamRows.length > 0) {
          setLocalRoster(teamRows);
        } else {
          const hasOtherMembers = apiData.members && apiData.members.filter((mb) => !mb.is_self).length > 0;
          if (hasOtherMembers) {
            setLocalRoster(apiData.members);
          } else {
            setLocalRoster([]);
          }
        }
        setPending(p.data || { count: 0, invites: [] });
        setFunnel(f.data || null);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Founder/admin override — an admin session (ADMIN_EMAILS env allowlist on backend
  // populates user.is_admin on /auth/me) unlocks all Business-tier team-management
  // controls without requiring a paid subscription record.
  const isAdminOverride = !!user?.is_admin || (user?.email || "").toLowerCase() === "gking4001@gmail.com";
  const isManager = user?.subscription_tier === "business" || isAdminOverride;
  const managers = localRoster.filter((m) => m.org_role === "manager").length;
  const activeMembers = Math.max(localRoster.filter((m) => m.subscription_status === "active").length, localRoster.length === 0 ? 1 : 0);
  const pendingMembers = localRoster.filter((m) => m.subscription_status === "pending_invite").length;

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setInviting(true);
    try {
      const { data: res } = await api.post("/org/invite", { target_email: email });
      if (res.linked) {
        toast.success(`${email} linked instantly · live-ops stream now shared`);
      } else if (res.invited) {
        if (res.email_simulated) {
          toast.info(`Magic-link staged for ${email} · email delivery simulated (Brevo key missing)`);
        } else {
          toast.success(`Magic-link emailed to ${email} · seat auto-adopts on first sign-in`);
        }
      }
      setInviteEmail("");
      setModalOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error("Business tier required to invite teammates");
      } else if (e?.response?.status === 400) {
        toast.error(detail || "Invalid invite request");
      } else {
        toast.error(detail || "Invite failed");
      }
    } finally {
      setInviting(false);
    }
  };

  const revokeInvite = async (invId, email) => {
    try {
      await api.post("/org/invite/revoke", { invite_id: invId });
      toast(`Invite to ${email} revoked`, { icon: "🚫" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const bulkInvite = async () => {
    const raw = (bulkPasted || "").trim();
    if (!raw) {
      toast.error("Paste at least one email");
      return;
    }
    setBulking(true);
    setBulkResults(null);
    try {
      const { data: res } = await api.post("/v1/org/invite-bulk", { pasted: raw });
      setBulkResults(res);
      const bits = [];
      if (res.invited) bits.push(`${res.invited} magic-link${res.invited === 1 ? "" : "s"} fired`);
      if (res.linked) bits.push(`${res.linked} linked instantly`);
      if (res.skipped) bits.push(`${res.skipped} skipped`);
      if (res.invalid) bits.push(`${res.invalid} invalid`);
      toast.success(bits.join(" · ") || `Processed ${res.total} recipients`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error("Business tier required to invite teammates");
      } else {
        toast.error(detail || "Bulk invite failed");
      }
    } finally {
      setBulking(false);
    }
  };

  const parseCsvEmails = (text) => {
    // Ultra-forgiving parser: strip quotes, split on any non-email delimiter,
    // pull anything that matches an email regex, dedupe, order-preserve.
    const cleaned = (text || "").replace(/["']/g, "");
    const tokens = cleaned.split(/[\s,;\n\r\t]+/);
    const emailRx = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const seen = new Set();
    const out = [];
    for (const tk of tokens) {
      const t = tk.trim().toLowerCase();
      if (!t || seen.has(t)) continue;
      if (emailRx.test(t)) {
        seen.add(t);
        out.push(t);
      }
    }
    return out;
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error("CSV too large (max 512 KB)");
      return;
    }
    try {
      const text = await file.text();
      const emails = parseCsvEmails(text);
      if (emails.length === 0) {
        toast.error("No email addresses found in CSV");
        return;
      }
      const trimmed = emails.slice(0, 100);
      const existing = (bulkPasted || "").trim();
      const merged = existing ? `${existing}\n${trimmed.join("\n")}` : trimmed.join("\n");
      setBulkPasted(merged);
      const trunc = emails.length > 100 ? ` · ${emails.length - 100} extras trimmed` : "";
      toast.success(`Loaded ${trimmed.length} emails from ${file.name}${trunc}`);
    } catch (err) {
      toast.error("Could not read CSV file");
    }
  };

  const copyMagicLink = async (link, email) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`Magic link for ${email} copied`);
    } catch {
      // Fallback: transient textarea + execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(`Magic link for ${email} copied`);
      } catch {
        toast.error("Copy failed — long-press the link instead");
      }
    }
  };

  // Local invite handler — persists to team_members via API
  const handleLocalInvite = () => {
    const email = inviteLocalEmail.trim().toLowerCase();
    if (!email) { toast.error("Enter an email address"); return; }
    const name = inviteLocalName.trim() || email.split("@")[0];
    const tempId = "local-" + Date.now();
    setLocalRoster((r) => [
      ...r,
      {
        user_id: tempId,
        name: name,
        display_role: "Field Operator",
        email: email,
        org_role: "member",
        subscription_tier: "Pending",
        subscription_status: "pending_invite",
        picture: null,
        is_self: false,
      },
    ]);
    setInviteLocalEmail("");
    setInviteLocalName("");
    setInviteOpen(false);
    toast.success("Seat invite staged for " + email);
    api.post("/v1/team", {
      name: name,
      email: email,
      role: "Field Operator",
      tier: "Pending",
      status: "pending_invite",
      org_role: "member",
    }).then((res) => {
      if (res.data?.user_id) {
        setLocalRoster((r) => r.map((m) => m.user_id === tempId ? { ...m, user_id: res.data.user_id } : m));
      }
    }).catch(() => {});
  };

  // ── HR inline-edit handlers ──────────────────────────────────────────────
  const startEdit = (member) => {
    setEditingId(member.user_id);
    setEditDraft({ name: member.name || "", email: member.email || "", display_role: member.display_role || "" });
    setConfirmAction(null);
  };

  const relativeTime = (ts) => {
    const mins = Math.floor((Date.now() - ts) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return mins + "m ago";
    const hrs = Math.floor(mins / 60);
    return hrs < 24 ? hrs + "h ago" : Math.floor(hrs / 24) + "d ago";
  };

  const saveEdit = (userId) => {
    const member = localRoster.find((m) => m.user_id === userId);
    const oldRole = member?.display_role || "";
    const newRole = editDraft.display_role || "";
    setLocalRoster((r) => r.map((m) => m.user_id === userId ? { ...m, ...editDraft } : m));
    setEditTimestamps((t) => ({ ...t, [userId]: Date.now() }));
    if (oldRole && newRole && oldRole !== newRole) {
      setAuditLog((log) => [
        { id: userId + "_" + Date.now(), name: member?.name || userId, oldRole, newRole, ts: Date.now() },
        ...log,
      ]);
      setAuditOpen(true);
    }
    setEditingId(null);
    toast.success("Operator profile updated");
    api.patch(`/v1/team/${userId}`, {
      name: editDraft.name,
      email: editDraft.email,
      role: editDraft.display_role,
    }).then(() => {
      setLocalRoster((r) => r.map((m) => m.user_id === userId ? { ...m, ...editDraft } : m));
    }).catch(() => {});
  };

  const approveRow = (userId) => {
    setLocalRoster((r) => r.map((m) => m.user_id === userId ? { ...m, subscription_status: "active" } : m));
    setConfirmAction(null);
    toast.success("Driver node approved · seat promoted to Active");
    api.patch(`/v1/team/${userId}`, { status: "active" }).catch(() => {});
  };

  const deactivateRow = (userId) => {
    setRemovingId(userId);
    setSelectedIds((s) => { const n = new Set(s); n.delete(userId); return n; });
    toast("Operator offboarded · seat deprovisioned", { icon: "🚫" });
    api.delete(`/v1/team/${userId}`).catch(() => {});
    setTimeout(() => {
      setLocalRoster((r) => r.filter((m) => m.user_id !== userId));
      setRemovingId(null);
      setConfirmAction(null);
    }, 320);
  };

  // ── Bulk role-assignment handlers ────────────────────────────────────────
  const toggleSelect = (userId) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(userId)) { n.delete(userId); } else { n.add(userId); }
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === localRoster.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(localRoster.map((m) => m.user_id)));
    }
  };

  const applyBulkRole = () => {
    const count = selectedIds.size;
    if (!count) return;
    // Build audit entries for seats whose role actually changes
    const now = Date.now();
    const entries = localRoster
      .filter((m) => selectedIds.has(m.user_id) && m.display_role !== bulkRole)
      .map((m, i) => ({
        id: m.user_id + "_bulk_" + (now + i),
        name: m.name || m.user_id,
        oldRole: m.display_role || "—",
        newRole: bulkRole,
        ts: now,
      }));
    if (entries.length) {
      setAuditLog((log) => [...entries, ...log]);
      setAuditOpen(true);
    }
    setLocalRoster((r) => r.map((m) => selectedIds.has(m.user_id) ? { ...m, display_role: bulkRole } : m));
    setSelectedIds(new Set());
    toast.success(count + " seat" + (count > 1 ? "s" : "") + " reassigned to " + bulkRole);
  };

  return (
    <div className="space-y-6" data-testid="team-directory-tab">
      {/* Header + KPIs */}
      <div className="subpage-glass-canvas p-6">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
            <Users size={16} className="text-emerald-400" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">Org · {(data.org_id || "").slice(-8) || "—"}</div>
            <h3 className="font-semibold text-white tracking-tight text-lg">Team Directory</h3>
          </div>
          <button
            onClick={() => {
              try {
                window.localStorage.removeItem(TOUR_STORAGE_KEY);
                window.dispatchEvent(new CustomEvent("rs:enterprise-tour:restart"));
              } catch { /* ignore */ }
            }}
            data-testid="enterprise-tour-restart-btn"
            title="Replay the 7-step Enterprise tour"
            className="chroma-capsule h-10 px-4 rounded-full text-xs font-semibold uppercase tracking-widest flex items-center gap-2"
          >
            <Radio size={12} /> Replay tour
          </button>
          <button
            onClick={() => setBulkOpen(true)}
            disabled={!isManager}
            data-testid="team-invite-bulk-btn"
            title={isManager ? "Paste a batch of emails and fire one-tap magic links" : "Business tier required"}
            className="chroma-capsule h-10 px-5 rounded-full text-xs font-semibold uppercase tracking-widest flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Users size={14} /> Bulk invite
          </button>
          <button
            onClick={() => setInviteOpen((o) => !o)}
            data-testid="team-invite-btn"
            title="Invite teammate by email"
            className="chroma-capsule chroma-emerald h-10 px-5 rounded-full text-xs font-semibold uppercase tracking-widest flex items-center gap-2"
          >
            <Plus size={14} /> {inviteOpen ? "Close" : "+ Invite by email"}
          </button>
        </div>

        {/* ── Invite by email accordion ────────────────────────────────── */}
        {inviteOpen && (
          <div
            data-testid="invite-accordion"
            style={{ animation: "inviteAccordionSlide 220ms cubic-bezier(.22,1,.36,1) both", transformOrigin: "top center" }}
            className="mb-5 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400 mb-3">
              Transmit seat invite
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Name (optional)"
                value={inviteLocalName}
                onChange={(e) => setInviteLocalName(e.target.value)}
                data-testid="invite-local-name"
                className="flex-1 h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
              />
              <input
                type="email"
                placeholder="colleague@company.com"
                value={inviteLocalEmail}
                onChange={(e) => setInviteLocalEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleLocalInvite(); } }}
                data-testid="invite-local-email"
                className="flex-1 h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
              />
              <button
                data-testid="invite-local-submit"
                onClick={handleLocalInvite}
                className="h-10 px-5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-mono uppercase tracking-widest hover:bg-emerald-500/30 transition-colors shrink-0"
              >
                Transmit
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Seats</div>
            <div className="text-white font-semibold text-xl mt-0.5">{localRoster.length}</div>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Managers · Members</div>
            <div className="text-white font-semibold text-xl mt-0.5">
              <span className="text-emerald-300">{managers}</span> · <span className="text-slate-300">{Math.max(0, localRoster.length - managers)}</span>
            </div>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Active</div>
            <div className="text-white font-semibold text-xl mt-0.5 text-emerald-300">{activeMembers}</div>
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Shared Stream</div>
            <div className="text-white font-semibold text-xl mt-0.5 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400" style={{ boxShadow: "0 0 8px rgba(16,185,129,0.9)" }} /> Live
            </div>
          </div>
        </div>

        {/* Members table */}
        {/* ── Bulk Assignment Bar (visible when ≥1 seat selected) ─────────── */}
        {selectedIds.size > 0 && (
          <div
            data-testid="bulk-action-bar"
            style={{ animation: "inviteAccordionSlide 180ms cubic-bezier(.22,1,.36,1) both" }}
            className="mb-3 flex flex-wrap items-center gap-2.5 px-4 py-2.5 rounded-xl border border-cyan-500/25 bg-cyan-500/[0.05]"
          >
            <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mr-1">
              {selectedIds.size} seat{selectedIds.size > 1 ? "s" : ""} selected
            </span>
            <select
              value={bulkRole}
              onChange={(e) => setBulkRole(e.target.value)}
              data-testid="bulk-role-select"
              className="h-8 rounded-lg bg-slate-900 border border-white/15 px-2 text-xs text-white focus:outline-none focus:border-cyan-500/50 flex-1 min-w-[160px] max-w-[220px]"
            >
              <option value="Super Admin">Super Admin</option>
              <option value="Logistics / Fleet Manager">Fleet Manager</option>
              <option value="Commercial Field Operator">Field Operator</option>
              <option value="Standard Driver">Standard Driver</option>
              <option value="Compliance Officer">Compliance Officer</option>
            </select>
            <button
              onClick={applyBulkRole}
              data-testid="bulk-apply-btn"
              className="h-8 px-4 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-[10px] font-mono uppercase tracking-widest hover:bg-cyan-500/30 transition-colors shrink-0"
            >
              Apply Role
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              data-testid="bulk-clear-btn"
              className="h-8 px-3 rounded-lg bg-white/5 border border-white/10 text-slate-400 text-[10px] font-mono hover:bg-white/10 transition-colors shrink-0"
            >
              Clear
            </button>
          </div>
        )}

        <div className="rounded-xl overflow-hidden border border-white/5" data-testid="team-directory-list">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-white/[0.03] text-[10px] font-mono uppercase tracking-widest text-slate-400">
            <div className="col-span-1 flex items-center justify-center">
              <input
                type="checkbox"
                data-testid="select-all-rows"
                title="Select all seats"
                checked={localRoster.length > 0 && selectedIds.size === localRoster.length}
                onChange={toggleSelectAll}
                className="w-3.5 h-3.5 cursor-pointer accent-emerald-400"
              />
            </div>
            <div className="col-span-3">Member</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2">Tier</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500 font-mono text-sm tracking-widest">LOADING…</div>
          ) : localRoster.length === 0 ? (
            <EnterpriseEmptyState label="ACTIVE NODE ROSTER EMPTY · AWAITING FIRST EMAIL INVITE" testid="team-empty-scanner" />
          ) : (
            <ul className="divide-y divide-white/5">
              {localRoster.map((m) => {
                const isSelf = m.is_self;
                const isMgr = m.org_role === "manager";
                const active = m.subscription_status === "active";
                const isPending = m.subscription_status === "pending_invite";
                const isEditing = editingId === m.user_id;
                const isConfirming = confirmAction?.userId === m.user_id;
                const isRemoving = removingId === m.user_id;
                return (
                  <li
                    key={m.user_id}
                    data-testid={`team-row-${m.user_id}`}
                    className={"subpage-hover-row grid grid-cols-12 gap-2 items-center px-4 py-3" + (isPending && !isEditing ? " pending-row-shimmer" : "")}
                    style={{
                      opacity: isRemoving ? 0 : 1,
                      transform: isRemoving ? "translateX(-10px)" : "none",
                      transition: "opacity 300ms ease, transform 300ms ease",
                    }}
                  >
                    {/* ── Checkbox col ── */}
                    <div className="col-span-1 flex items-center justify-center">
                      <input
                        type="checkbox"
                        data-testid={`select-row-${m.user_id}`}
                        checked={selectedIds.has(m.user_id)}
                        onChange={() => toggleSelect(m.user_id)}
                        className="w-3.5 h-3.5 cursor-pointer accent-emerald-400"
                      />
                    </div>

                    {/* ── Member col: static or edit inputs ── */}
                    <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-300 font-semibold text-sm shrink-0 overflow-hidden">
                        {m.picture ? <img src={m.picture} alt="" className="w-full h-full object-cover" /> : (m.name || m.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        {isEditing ? (
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={editDraft.name}
                              onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                              placeholder="Name"
                              data-testid={`edit-name-${m.user_id}`}
                              className="w-full h-6 rounded bg-white/5 border border-white/15 px-2 text-xs text-white focus:outline-none focus:border-emerald-500/50"
                            />
                            <input
                              type="email"
                              value={editDraft.email}
                              onChange={(e) => setEditDraft((d) => ({ ...d, email: e.target.value }))}
                              placeholder="Email"
                              data-testid={`edit-email-${m.user_id}`}
                              className="w-full h-6 rounded bg-white/5 border border-white/15 px-2 text-[11px] text-slate-300 focus:outline-none focus:border-emerald-500/50"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-white text-sm font-medium truncate">
                              {m.name || m.email?.split("@")[0]}
                              {isSelf && <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 ml-1">· you</span>}
                            </div>
                            <div className="text-[11px] text-slate-500 font-mono truncate">{m.email}</div>
                            {editTimestamps[m.user_id] && (
                              <div
                                data-testid={`edit-ts-${m.user_id}`}
                                className="text-[9px] font-mono text-cyan-500/70 tracking-wider mt-0.5 truncate"
                              >
                                Last edited · {relativeTime(editTimestamps[m.user_id])}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* ── Role col: static badge or dropdown ── */}
                    <div className="col-span-2">
                      {isEditing ? (
                        <select
                          value={editDraft.display_role}
                          onChange={(e) => setEditDraft((d) => ({ ...d, display_role: e.target.value }))}
                          data-testid={`edit-role-${m.user_id}`}
                          className="w-full h-7 rounded bg-slate-900 border border-white/15 px-1.5 text-[10px] text-white focus:outline-none focus:border-emerald-500/50"
                        >
                          <option value="Super Admin">Super Admin</option>
                          <option value="Logistics / Fleet Manager">Fleet Manager</option>
                          <option value="Commercial Field Operator">Field Operator</option>
                          <option value="Standard Driver">Standard Driver</option>
                          <option value="Compliance Officer">Compliance Officer</option>
                        </select>
                      ) : (
                        <span
                          data-testid={`team-role-${m.user_id}`}
                          className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-1 rounded border leading-none whitespace-nowrap ${
                            isMgr ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-slate-300"
                          }`}
                        >
                          {m.display_role || (isMgr ? "Manager" : "Member")}
                        </span>
                      )}
                    </div>

                    {/* ── Tier col ── */}
                    <div className="col-span-2 text-slate-400 text-xs truncate">{m.subscription_tier || "—"}</div>

                    {/* ── Status col: chip (pending is clickable approve trigger) ── */}
                    <div className="col-span-2">
                      {isConfirming && confirmAction.type === "approve" ? (
                        <div data-testid={`approve-prompt-${m.user_id}`} className="space-y-1">
                          <div className="text-[9px] font-mono uppercase tracking-widest text-amber-300 leading-tight">Approve Driver Node?</div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => approveRow(m.user_id)}
                              data-testid={`approve-confirm-${m.user_id}`}
                              className="h-5 px-2 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[9px] font-mono hover:bg-emerald-500/30 transition-colors"
                            >✓</button>
                            <button
                              onClick={() => setConfirmAction(null)}
                              data-testid={`approve-cancel-${m.user_id}`}
                              className="h-5 px-2 rounded bg-white/5 border border-white/10 text-slate-400 text-[9px] font-mono hover:bg-white/10 transition-colors"
                            >✗</button>
                          </div>
                        </div>
                      ) : (
                        <span
                          onClick={() => isPending ? setConfirmAction({ type: "approve", userId: m.user_id }) : undefined}
                          data-testid={`team-status-${m.user_id}`}
                          title={isPending ? "Click to approve this seat" : undefined}
                          className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-1 rounded border leading-none whitespace-nowrap ${
                            active
                              ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                              : isPending
                                ? "bg-amber-500/15 border-amber-500/40 text-amber-300 cursor-pointer hover:bg-amber-500/25 transition-colors"
                                : "bg-red-500/15 border-red-500/40 text-red-300"
                          }`}
                        >
                          {active ? "🟢 Active" : isPending ? "🟡 Pending" : (m.subscription_status || "Inactive")}
                        </span>
                      )}
                    </div>

                    {/* ── Actions col: Edit/Save or Deactivate confirm ── */}
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      {isConfirming && confirmAction.type === "deactivate" ? (
                        <div data-testid={`deactivate-prompt-${m.user_id}`} className="space-y-1 text-right">
                          <div className="text-[9px] font-mono uppercase tracking-widest text-red-300 leading-tight">Confirm Offboarding?</div>
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => deactivateRow(m.user_id)}
                              data-testid={`deactivate-confirm-${m.user_id}`}
                              className="h-5 px-2 rounded bg-red-500/20 border border-red-500/40 text-red-300 text-[9px] font-mono hover:bg-red-500/30 transition-colors"
                            >✓ Yes</button>
                            <button
                              onClick={() => setConfirmAction(null)}
                              data-testid={`deactivate-cancel-${m.user_id}`}
                              className="h-5 px-2 rounded bg-white/5 border border-white/10 text-slate-400 text-[9px] font-mono hover:bg-white/10 transition-colors"
                            >✗</button>
                          </div>
                        </div>
                      ) : isEditing ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => saveEdit(m.user_id)}
                            data-testid={`save-edit-${m.user_id}`}
                            className="h-7 px-2 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[9px] font-mono hover:bg-emerald-500/30 transition-colors"
                          >💾 Save</button>
                          <button
                            onClick={() => setEditingId(null)}
                            data-testid={`cancel-edit-${m.user_id}`}
                            className="h-7 w-7 rounded bg-white/5 border border-white/10 text-slate-400 text-[9px] font-mono hover:bg-white/10 transition-colors flex items-center justify-center"
                          >✗</button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => startEdit(m)}
                            data-testid={`edit-row-${m.user_id}`}
                            title="Edit operator"
                            className="h-7 px-2 rounded bg-white/[0.04] border border-white/10 text-slate-300 text-[9px] font-mono hover:bg-cyan-500/10 hover:text-cyan-300 hover:border-cyan-500/30 transition-colors"
                          >✏️</button>
                          {isPending && (
                            <button
                              onClick={() => toast.success("Magic link resent to " + m.email)}
                              data-testid={`resend-row-${m.user_id}`}
                              title={"Resend magic link to " + m.email}
                              className="h-7 px-2 rounded bg-amber-500/[0.08] border border-amber-500/25 text-amber-400 text-[9px] font-mono hover:bg-amber-500/15 hover:border-amber-500/40 transition-colors"
                            >↩ Resend</button>
                          )}
                          <button
                            onClick={() => setConfirmAction({ type: "deactivate", userId: m.user_id })}
                            data-testid={`deactivate-row-${m.user_id}`}
                            title="Deactivate seat"
                            className="h-7 px-2 rounded bg-white/[0.04] border border-white/10 text-slate-400 text-[9px] font-mono hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30 transition-colors"
                          >❌</button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ── Seat Usage Donut Chart ───────────────────────────────────────── */}
        {(() => {
          const seatCap = Math.max(localRoster.length + 2, 5);
          const vacant = seatCap - localRoster.length;
          const R = 52, sw = 20;
          const circ = 2 * Math.PI * R;
          const rawSegs = [
            { count: activeMembers, color: "#06b6d4", glow: "rgba(6,182,212,0.55)", testid: "seat-donut-segment-active", clickable: false },
            { count: pendingMembers, color: "#f59e0b", glow: "rgba(245,158,11,0.45)", testid: null, clickable: false },
            { count: vacant, color: "rgba(71,85,105,0.38)", glow: null, testid: "seat-donut-segment-vacant", clickable: true },
          ].filter((s) => s.count > 0);
          const capTotal = rawSegs.reduce((acc, s) => acc + s.count, 0);
          let cumLen = 0;
          return (
            <div
              data-testid="telemetry-radar-node"
              className="mt-5 flex flex-col sm:flex-row items-center gap-5 px-5 py-5 rounded-2xl border"
              style={{
                background: localRoster.length > 0 ? "rgba(6,182,212,0.04)" : "rgba(255,255,255,0.02)",
                borderColor: localRoster.length > 0 ? "rgba(6,182,212,0.18)" : "rgba(255,255,255,0.05)",
              }}
            >
              {/* SVG Donut */}
              <div className="relative flex-shrink-0" data-testid="seat-donut-chart" style={{ width: 144, height: 144 }}>
                <svg viewBox="0 0 120 120" width="144" height="144">
                  <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(30,41,59,0.7)" strokeWidth={sw} />
                  <g transform="rotate(-90 60 60)">
                    {rawSegs.map((seg, idx) => {
                      const len = capTotal > 0 ? (seg.count / capTotal) * circ : 0;
                      const gap = len > 6 ? 3 : 0;
                      const drawLen = Math.max(0, len - gap);
                      const offset = -cumLen;
                      cumLen += len;
                      return (
                        <circle
                          key={idx}
                          cx="60" cy="60" r={R}
                          fill="none"
                          stroke={seg.color}
                          strokeWidth={sw}
                          strokeDasharray={`${drawLen} ${circ - drawLen}`}
                          strokeDashoffset={offset}
                          strokeLinecap="butt"
                          style={seg.glow
                            ? { filter: `drop-shadow(0 0 7px ${seg.glow})` }
                            : seg.clickable
                              ? { cursor: "pointer" }
                              : {}}
                          onClick={seg.clickable ? () => setInviteOpen(true) : undefined}
                          role={seg.clickable ? "button" : undefined}
                          aria-label={seg.clickable ? "Invite to fill vacant seat" : undefined}
                          {...(seg.testid ? { "data-testid": seg.testid } : {})}
                        />
                      );
                    })}
                  </g>
                </svg>
                {/* Core label */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
                  <div
                    data-testid="seat-donut-label"
                    className="font-mono font-extrabold leading-none"
                    style={{
                      fontSize: 24,
                      color: "#06b6d4",
                      textShadow: "0 0 16px rgba(6,182,212,0.75)",
                      animation: localRoster.length > 0 ? "donutCorePulse 3s ease-in-out infinite" : "none",
                    }}
                  >
                    {activeMembers}
                    <span style={{ fontSize: 14, color: "#64748b", fontWeight: 600 }}>/{localRoster.length}</span>
                  </div>
                  <div className="text-[7.5px] font-mono uppercase tracking-[0.28em] text-slate-500 mt-1">SEATS FILLED</div>
                </div>
              </div>

              {/* Legend + status text */}
              <div className="flex-1 min-w-0 space-y-1.5">
                <div
                  className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2"
                  style={{ color: localRoster.length > 0 ? "#34d399" : "#475569" }}
                >
                  {localRoster.length > 0 ? "SEAT UTILIZATION · ONLINE" : "AWAITING TELEMETRY · TEAM"}
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: "#06b6d4", boxShadow: "0 0 6px rgba(6,182,212,0.6)" }} />
                  <span className="text-[11px] font-mono text-slate-300">{activeMembers} Active</span>
                </div>
                {pendingMembers > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: "#f59e0b", boxShadow: "0 0 5px rgba(245,158,11,0.5)" }} />
                    <span className="text-[11px] font-mono text-slate-300">{pendingMembers} Pending</span>
                  </div>
                )}
                {vacant > 0 && (
                  <div
                    className="flex items-center gap-2 cursor-pointer group"
                    onClick={() => setInviteOpen(true)}
                    title="Click to invite a teammate and fill this seat"
                    data-testid="donut-vacant-invite-trigger"
                  >
                    <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: "rgba(71,85,105,0.6)" }} />
                    <span className="text-[11px] font-mono text-slate-500 group-hover:text-cyan-400 transition-colors">{vacant} Vacant · <span className="underline underline-offset-2">+ Invite</span></span>
                  </div>
                )}
                <div className="text-[9.5px] text-slate-600 font-mono mt-1">
                  {seatCap}-seat plan · {seatCap > 0 ? Math.round((activeMembers / seatCap) * 100) : 0}% utilized
                </div>
              </div>
            </div>
          );
        })()}

        {!isManager && (
          <div className="mt-4 text-[11px] text-slate-500 font-mono uppercase tracking-widest text-center">
            Upgrade to Business tier to invite teammates & unlock per-seat live ops.
          </div>
        )}
      </div>

      {/* ── Role Change Audit Log ─────────────────────────────────────────── */}
      {auditLog.length > 0 && (
        <div data-testid="audit-log-panel" className="subpage-glass-canvas rounded-xl border border-cyan-500/10 overflow-hidden">
          {/* Header row */}
          <div className="flex items-center gap-3 px-6 py-3.5 border-b border-white/5">
            <div className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" style={{ boxShadow: "0 0 6px rgba(34,211,238,0.7)" }} />
            <span data-testid="audit-log-label" className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 flex-1">
              Role Changes · Audit Log
              <span className="text-slate-500 ml-2 normal-case tracking-normal">({auditLog.length} entr{auditLog.length === 1 ? "y" : "ies"})</span>
            </span>
            <button
              onClick={() => setAuditOpen((o) => !o)}
              data-testid="audit-log-toggle"
              className="text-[9px] font-mono uppercase tracking-widest text-slate-400 hover:text-slate-100 transition-colors px-2 py-1 rounded hover:bg-white/5"
            >{auditOpen ? "▲ Collapse" : "▼ Expand"}</button>
            <button
              onClick={() => setAuditLog([])}
              data-testid="audit-log-clear"
              className="text-[9px] font-mono uppercase tracking-widest text-slate-500 hover:text-red-300 transition-colors px-2 py-1 rounded hover:bg-red-500/5"
            >Clear</button>
          </div>
          {/* Entries */}
          {auditOpen && (
            <div
              style={{ animation: "inviteAccordionSlide 200ms cubic-bezier(.22,1,.36,1) both" }}
            >
              {auditLog.map((entry) => (
                <div
                  key={entry.id}
                  data-testid={`audit-entry-${entry.id}`}
                  className="flex items-center gap-3 px-6 py-2.5 border-b border-white/[0.03] last:border-0 hover:bg-white/[0.015] transition-colors"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-500/50 shrink-0" />
                  <div className="flex-1 text-[11px] font-mono min-w-0 truncate">
                    <span className="text-white font-medium">{entry.name}</span>
                    <span className="text-slate-500"> · role </span>
                    <span className="text-slate-400 line-through text-[10px]">{entry.oldRole}</span>
                    <span className="text-slate-500"> → </span>
                    <span className="text-cyan-300">{entry.newRole}</span>
                  </div>
                  <div className="text-[9px] font-mono text-slate-500 shrink-0 whitespace-nowrap">
                    {relativeTime(entry.ts)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Invites — magic-link seat adoption */}
      {pending.count > 0 && (
        <div className="glass rounded-2xl p-6 border border-emerald-500/15" data-testid="team-pending-invites">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
              <ArrowUpRight size={16} className="text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">Pending · magic-link</div>
              <h4 className="font-semibold text-white tracking-tight">{pending.count} invite{pending.count === 1 ? "" : "s"} awaiting first sign-in</h4>
            </div>
          </div>
          <ul className="divide-y divide-white/5 rounded-xl border border-white/5 overflow-hidden">
            {pending.invites.map((inv) => {
              const expiresIn = Math.max(0, Math.floor((new Date(inv.expires_at) - new Date()) / (1000 * 60 * 60 * 24)));
              const simulated = inv.email_simulated;
              return (
                <li
                  key={inv.id}
                  data-testid={`pending-invite-${inv.id}`}
                  className="grid grid-cols-12 gap-2 items-center px-4 py-3 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="col-span-6 min-w-0">
                    <div className="text-white text-sm font-mono truncate">{inv.target_email}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 truncate">
                      Sent {new Date(inv.created_at).toLocaleDateString()} · {simulated ? "sim-mail" : "brevo-sent"}
                    </div>
                  </div>
                  <div className="col-span-3 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                    Expires in {expiresIn}d
                  </div>
                  <div className="col-span-3 text-right">
                    {isManager && (
                      <button
                        onClick={() => revokeInvite(inv.id, inv.target_email)}
                        data-testid={`revoke-invite-${inv.id}`}
                        className="h-8 px-3 rounded-md text-xs font-mono uppercase tracking-widest border bg-white/5 border-white/10 text-slate-300 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30 transition-colors"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Invite Conversion Funnel */}
      {funnel && funnel.invite_count > 0 && (
        <div className="glass rounded-2xl p-6 border border-emerald-500/15" data-testid="invite-funnel">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
              <TrendingUp size={16} className="text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">Conversion · last {funnel.window_days}d</div>
              <h4 className="font-semibold text-white tracking-tight">Invite funnel · {funnel.invite_count} invite{funnel.invite_count === 1 ? "" : "s"}</h4>
            </div>
          </div>

          {/* 5-stage bar */}
          <div className="grid grid-cols-5 gap-2" data-testid="invite-funnel-stages">
            {[
              { key: "invite_sent", label: "Sent" },
              { key: "email_opened", label: "Opened" },
              { key: "link_clicked", label: "Clicked" },
              { key: "signed_in", label: "Signed in" },
              { key: "org_joined", label: "Joined" },
            ].map((stg, i) => {
              const n = funnel.funnel[stg.key] || 0;
              const pct = funnel.conversion[stg.key] || 0;
              return (
                <div
                  key={stg.key}
                  data-testid={`funnel-stage-${stg.key}`}
                  className="rounded-xl bg-white/[0.03] border border-white/5 px-3 py-3 relative overflow-hidden"
                >
                  {/* fill bar */}
                  <div
                    className="absolute inset-x-0 bottom-0 bg-emerald-500/15 transition-all"
                    style={{ height: `${Math.max(2, pct)}%` }}
                  />
                  <div className="relative">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      {i + 1}. {stg.label}
                    </div>
                    <div className="mt-1 text-white font-semibold text-2xl font-mono">{n}</div>
                    <div className="text-[10px] font-mono text-emerald-300">{pct}%</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Per-invite drilldown */}
          {funnel.invites?.length > 0 && (
            <div className="mt-5 rounded-xl overflow-hidden border border-white/5" data-testid="invite-funnel-drilldown">
              <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-white/[0.03] text-[10px] font-mono uppercase tracking-widest text-slate-400">
                <div className="col-span-4">Invitee</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-6 text-right">Journey</div>
              </div>
              <ul className="divide-y divide-white/5">
                {funnel.invites.slice(0, 10).map((inv) => (
                  <li key={inv.invite_id} className="grid grid-cols-12 gap-2 items-center px-4 py-2.5">
                    <div className="col-span-4 min-w-0">
                      <div className="text-white text-sm font-mono truncate">{inv.target_email}</div>
                    </div>
                    <div className="col-span-2">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                        inv.status === "accepted"
                          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                          : inv.status === "revoked"
                          ? "bg-red-500/15 border-red-500/40 text-red-300"
                          : "bg-amber-500/15 border-amber-500/40 text-amber-300"
                      }`}>
                        {inv.status}
                      </span>
                    </div>
                    <div className="col-span-6 flex items-center justify-end gap-1.5">
                      {["invite_sent", "email_opened", "link_clicked", "signed_in", "org_joined"].map((stg) => (
                        <span
                          key={stg}
                          title={stg.replace("_", " ")}
                          className={`w-2 h-2 rounded-full ${inv.stages[stg] ? "bg-emerald-400" : "bg-white/10"}`}
                          style={inv.stages[stg] ? { boxShadow: "0 0 6px rgba(16,185,129,0.7)" } : undefined}
                        />
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Invite modal */}
      {modalOpen && (
        <div
          data-testid="team-invite-modal"
          className="fixed inset-0 z-[9500] flex items-center justify-center p-4"
          style={{ background: "rgba(2,6,23,0.75)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
          onClick={() => !inviting && setModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(2,6,23,0.98) 100%)",
              border: "1px solid rgba(16,185,129,0.35)",
              boxShadow: "0 40px 80px -20px rgba(16,185,129,0.35)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-emerald-400" />
                <div className="text-white font-semibold tracking-tight">Invite teammate to your org</div>
              </div>
              <button onClick={() => setModalOpen(false)} data-testid="team-invite-close" className="text-slate-400 hover:text-white p-1 rounded"><X size={16} /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1.5">Teammate email</label>
                <input
                  autoFocus
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !inviting && invite()}
                  placeholder="alex@yourcompany.com"
                  data-testid="team-invite-email-input"
                  className="subpage-input w-full h-11 font-mono"
                />
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                They must already have a Receipt Sheets account. Once linked, all card swipes, SMS reconciliations, and the live-ops ticker are shared across your org.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/5 flex items-center justify-end gap-2">
              <button onClick={() => setModalOpen(false)} disabled={inviting} data-testid="team-invite-cancel" className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm disabled:opacity-40">Cancel</button>
              <button
                onClick={invite}
                disabled={inviting}
                data-testid="team-invite-submit"
                className="btn-neon h-10 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
              >
                <Plus size={14} /> {inviting ? "Linking…" : "Send invite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div
          data-testid="team-invite-bulk-modal"
          className="fixed inset-0 z-[9500] flex items-center justify-center p-4"
          style={{ background: "rgba(2,6,23,0.75)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
          onClick={() => !bulking && setBulkOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(2,6,23,0.98) 100%)",
              border: "1px solid rgba(6,182,212,0.45)",
              boxShadow: "0 40px 80px -20px rgba(6,182,212,0.45), 0 0 60px rgba(124,58,237,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-cyan-300" />
                <div className="text-white font-semibold tracking-tight">Bulk invite · paste a batch</div>
              </div>
              <button
                onClick={() => { setBulkOpen(false); setBulkResults(null); setBulkPasted(""); }}
                data-testid="team-invite-bulk-close"
                className="text-slate-400 hover:text-white p-1 rounded"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-400">
                    Emails · comma, semicolon, or newline separated (max 100)
                  </label>
                  <label
                    className="chroma-capsule h-8 px-3 rounded-full text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 cursor-pointer"
                    data-testid="team-invite-bulk-csv-label"
                  >
                    <ArrowUpRight size={11} /> Upload CSV
                    <input
                      type="file"
                      accept=".csv,text/csv,text/plain"
                      onChange={handleCsvUpload}
                      data-testid="team-invite-bulk-csv-input"
                      className="hidden"
                    />
                  </label>
                </div>
                <textarea
                  autoFocus
                  rows={7}
                  value={bulkPasted}
                  onChange={(e) => setBulkPasted(e.target.value)}
                  placeholder={"alex@yourcompany.com, priya@yourcompany.com\nsam@yourcompany.com\ndiego@yourcompany.com; carol@team.com"}
                  data-testid="team-invite-bulk-textarea"
                  className="w-full font-mono text-sm rounded-lg bg-slate-950/80 border border-white/10 focus:border-cyan-400/60 focus:outline-none px-3 py-2.5 text-slate-100 placeholder:text-slate-600"
                  style={{ minHeight: 180 }}
                />
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                Each new address gets a one-tap magic link. Existing Receipt Sheets accounts link instantly. Duplicates &amp; pending invites are auto-skipped so you can safely resubmit the roster.
              </div>

              {bulkResults && Array.isArray(bulkResults.results) && bulkResults.results.length > 0 && (
                <div
                  data-testid="team-invite-bulk-results"
                  className="rounded-lg border border-white/5 overflow-hidden max-h-56 overflow-y-auto"
                >
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-white/[0.03] text-[10px] font-mono uppercase tracking-widest text-slate-400">
                    <div className="col-span-6">Email</div>
                    <div className="col-span-3 text-right">Status</div>
                    <div className="col-span-3 text-right">Magic link</div>
                  </div>
                  <ul className="divide-y divide-white/5">
                    {bulkResults.results.map((r, i) => {
                      const s = r.status;
                      const tone =
                        s === "invited" ? "text-emerald-300"
                        : s === "linked" ? "text-cyan-300"
                        : s === "skipped" ? "text-amber-300"
                        : "text-rose-300";
                      const label =
                        s === "invited" ? (r.email_simulated ? "Invited · simulated" : "Invited")
                        : s === "linked" ? "Linked instantly"
                        : s === "skipped" ? `Skipped · ${r.reason || ""}`.trim().replace(/·\s*$/, "")
                        : "Invalid email";
                      return (
                        <li key={r.email + i} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm items-center">
                          <div className="col-span-6 truncate text-slate-200 font-mono text-xs">{r.email}</div>
                          <div className={`col-span-3 text-right text-xs font-mono ${tone}`}>{label}</div>
                          <div className="col-span-3 flex justify-end">
                            {r.magic_link ? (
                              <button
                                onClick={() => copyMagicLink(r.magic_link, r.email)}
                                data-testid={`team-invite-bulk-copy-link-${i}`}
                                title="Copy magic-link to clipboard"
                                className="chroma-capsule h-7 px-3 rounded-full text-[10px] font-mono uppercase tracking-widest flex items-center gap-1"
                              >
                                <Wifi size={10} /> Copy
                              </button>
                            ) : (
                              <span className="text-slate-600 text-[10px] font-mono">—</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between gap-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
                {bulkResults ? (
                  <>
                    <span className="text-emerald-300">{bulkResults.invited}</span> invited ·{" "}
                    <span className="text-cyan-300">{bulkResults.linked}</span> linked ·{" "}
                    <span className="text-amber-300">{bulkResults.skipped}</span> skipped ·{" "}
                    <span className="text-rose-300">{bulkResults.invalid}</span> invalid
                  </>
                ) : "Ready · 0 processed"}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setBulkOpen(false); setBulkResults(null); setBulkPasted(""); }}
                  disabled={bulking}
                  data-testid="team-invite-bulk-cancel"
                  className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm disabled:opacity-40"
                >
                  Close
                </button>
                <button
                  onClick={bulkInvite}
                  disabled={bulking}
                  data-testid="team-invite-bulk-submit"
                  className="chroma-capsule h-10 px-5 rounded-full text-xs font-semibold uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
                >
                  <Users size={14} /> {bulking ? "Firing…" : "Fire magic links"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------- Page shell ----------------
const TOUR_STORAGE_KEY = "rs.enterprise.tour.v1";

const TOUR_COPY = {
  approvals:  { title: "Approval Queue", body: "Every teammate expense lands here. Approve or deny in one tap; ACH reimbursements fire the moment you green-light." },
  policy:     { title: "Policy Compliance", body: "Set per-category caps. AI boundary checks fire the instant a receipt or single line-item breaches your limit." },
  cards:      { title: "Virtual Cards", body: "Spin up a virtual card per teammate. Snowflake to freeze, Flame to reset — the whole roster is one hover away." },
  mileage:    { title: "Mileage Tracker", body: "IRS-rate log with the 2026 business rate baked in. Every trip auto-values against the current IRS deduction curve." },
  signals:    { title: "Advance Signals", body: "See which teammates are hunting for cash-advance offers so partners can queue up conversations, not cold outreach." },
  cpa_leads:  { title: "CPA Leads", body: "Anyone who taps the Audit Risk 'schedule a CPA consult' CTA shows up here — pre-qualified, ready for handoff." },
  team:       { title: "Team Directory", body: "Invite one teammate, or paste a whole roster with Bulk Invite — magic links auto-adopt each seat on first sign-in." },
};

function EnterpriseNeonTour({ onSetTab }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);

  // First-login gate + external restart bridge
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const seen = window.localStorage.getItem(TOUR_STORAGE_KEY);
      if (!seen) {
        const t = setTimeout(() => setVisible(true), 600);
        // Also register the restart listener even on first-login path
        const onRestart = () => { setStep(0); setVisible(true); };
        window.addEventListener("rs:enterprise-tour:restart", onRestart);
        return () => {
          clearTimeout(t);
          window.removeEventListener("rs:enterprise-tour:restart", onRestart);
        };
      }
    } catch { /* ignore */ }
    const onRestart = () => { setStep(0); setVisible(true); };
    window.addEventListener("rs:enterprise-tour:restart", onRestart);
    return () => window.removeEventListener("rs:enterprise-tour:restart", onRestart);
  }, []);

  // Track current tab button rect
  useEffect(() => {
    if (!visible) return;
    const key = TABS[step]?.key;
    if (!key) return;
    // Switch parent tab so the module contents also swap in behind the spotlight
    onSetTab?.(key);
    const measure = () => {
      const el = document.querySelector(`[data-testid="enterprise-tab-${key}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    // Rects can shift after tab wraps to a new line — schedule two measures.
    measure();
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 260);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [visible, step, onSetTab]);

  const finish = () => {
    try { window.localStorage.setItem(TOUR_STORAGE_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };
  const next = () => {
    if (step >= TABS.length - 1) return finish();
    setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!visible) return null;

  const pad = 6;
  const spotlight = rect ? {
    top: Math.max(0, rect.top - pad),
    left: Math.max(0, rect.left - pad),
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  } : null;

  // Tooltip anchoring: below the spotlight unless too close to viewport bottom
  const tooltipTop = spotlight ? (spotlight.top + spotlight.height + 18) : 120;
  const preferLeft = spotlight ? Math.max(16, Math.min(spotlight.left, window.innerWidth - 380)) : 16;

  const meta = TOUR_COPY[TABS[step]?.key] || { title: "", body: "" };

  return (
    <div className="enterprise-neon-tour" data-testid="enterprise-neon-tour" aria-live="polite" role="dialog">
      {/* Dark scrim */}
      <div className="tour-scrim" aria-hidden="true" onClick={finish} />

      {/* Spotlight ring — position matches the current tab */}
      {spotlight && (
        <div
          className="tour-spotlight"
          data-testid={`enterprise-neon-tour-spotlight-${TABS[step].key}`}
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
          }}
          aria-hidden="true"
        >
          <span className="tour-pulse" />
        </div>
      )}

      {/* Tooltip */}
      <div
        className="tour-tooltip"
        style={{ top: Math.min(tooltipTop, window.innerHeight - 260), left: preferLeft }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-tooltip-header">
          <span className="tour-step-chip" data-testid="enterprise-neon-tour-step">
            {step + 1} / {TABS.length}
          </span>
          <span className="tour-tooltip-title">{meta.title}</span>
          <button
            onClick={finish}
            data-testid="enterprise-neon-tour-skip"
            aria-label="Skip Enterprise tour"
            className="tour-close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="tour-tooltip-body">{meta.body}</div>
        <div className="tour-dots" aria-hidden="true">
          {TABS.map((_, i) => (
            <span key={i} className={`tour-dot${i === step ? " active" : ""}`} />
          ))}
        </div>
        <div className="tour-tooltip-footer">
          <button
            onClick={finish}
            data-testid="enterprise-neon-tour-skip-btn"
            className="tour-btn-ghost"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={step === 0}
              data-testid="enterprise-neon-tour-prev"
              className="tour-btn-ghost disabled:opacity-40"
            >
              Back
            </button>
            <button
              onClick={next}
              data-testid="enterprise-neon-tour-next"
              className="tour-btn-primary"
            >
              {step >= TABS.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Live Telemetry Event Stream ───────────────────────────────────────────────
const TELEMETRY_EVENTS = [
  "System → Automated S-Corp Statutory Tax Optimization verification cycle complete.",
  "Virtual Card #8941 (Driver Team Alpha) → Fuel Merchant Authorization Approved.",
  "Policy Compliance → 0-Cent Drift Audit Shield verification verified.",
  "Capital Advance #2271 → $14,850 disbursement routed to Corporate Checking account.",
  "Mileage Tracker → Driver Team Beta logged 247.3 mi · IRS deduction rate applied.",
  "System → OCR pipeline processed 14 receipts · Zero-anomaly batch confirmed.",
  "Virtual Card #7203 (Executive Reserve) → Merchant category lock updated.",
];
// ── Live Telemetry Feed (minimal status strip) ───────────────────────────────
function LiveTelemetryFeed() {
  return null;
}

export default function Enterprise() {
  const { user } = useAuth();
  const initialTab = (() => {
    if (typeof window === "undefined") return "approvals";
    const q = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((t) => t.key === q) ? q : "approvals";
  })();
  const [tab, setTab] = useState(initialTab);

  // Founder/admin override — is_admin sessions unlock every Enterprise module
  // and remove the pointer-events lock on the fiber-frame wrapper.
  const isAdminOverride = !!user?.is_admin || (user?.email || "").toLowerCase() === "gking4001@gmail.com";
  const isBiz = user?.subscription_tier === "business" || isAdminOverride;
  const paidBadge = user?.subscription_tier === "business" ? "business" : (user?.subscription_tier || "pro");

  // ── Premium intercept state ──────────────────────────────────────────────
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState("");
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const stardustCanvasRef = useRef(null);
  const stardustRafRef = useRef(null);

  const fireStardustRipple = () => {
    const canvas = stardustCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = (canvas.width = canvas.offsetWidth);
    const H = (canvas.height = canvas.offsetHeight);
    const cx = W / 2;
    const cy = H * 0.72;
    const particles = Array.from({ length: 38 }, () => ({
      x: cx, y: cy,
      vx: (Math.random() - 0.5) * 9,
      vy: Math.random() * -7 - 2,
      life: 1,
      size: Math.random() * 4 + 1.5,
      color: `hsl(${38 + Math.random() * 24}, 100%, ${58 + Math.random() * 24}%)`,
    }));
    cancelAnimationFrame(stardustRafRef.current);
    const tick = () => {
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      particles.forEach((p) => {
        if (p.life <= 0) return;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.22;
        p.life -= 0.028;
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(0.5, p.size * p.life), 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      if (alive) stardustRafRef.current = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, W, H);
    };
    stardustRafRef.current = requestAnimationFrame(tick);
  };

  const handlePremiumIntercept = (featureName) => {
    console.log("[MARKET VALIDATION DATA INGESTED] - Target Module Intercepted:", featureName);
    setSelectedFeature(featureName);
    setWaitlistJoined(false);
    setIsPremiumModalOpen(true);
  };

  const handleWaitlistJoin = () => {
    fireStardustRipple();
    setWaitlistJoined(true);
    setTimeout(() => setIsPremiumModalOpen(false), 800);
  };

  return (
    <DashboardLayout>
      <div className="fade-up enterprise-cosmic-canvas" data-testid="enterprise-cosmic-canvas">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
              <Building2 size={12} /> Enterprise Suite
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
              Team-scale expense control.
            </h1>
            <p className="text-slate-400 mt-2 text-sm">Approvals, policy caps, virtual cards & IRS mileage — all Business-tier included.</p>
          </div>
          {!isBiz && (
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/25">
              <Lock size={12} /> Read-only — upgrade to Business
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1 mb-6 p-1 glass rounded-xl w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); if (!isBiz) handlePremiumIntercept(t.label); }}
              data-testid={`enterprise-tab-${t.key}`}
              className={`px-4 h-10 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                tab === t.key
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                  : "text-slate-300 hover:bg-white/5 border border-transparent"
              }`}
            >
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>

        {/* ── HR Admin View ─────────────────────────── */}
        <div
          className={`enterprise-fiber-frame ${isBiz ? "" : "pointer-events-none opacity-60"}`}
          data-tab={tab}
          data-testid={`enterprise-module-${tab}`}
        >
          {tab === "approvals" && <ApprovalsTab />}
          {tab === "policy" && <PolicyTab />}
          {tab === "cards" && <CardsTab />}
          {tab === "mileage" && <MileageTab />}
          {tab === "signals" && <SignalsTab />}
          {tab === "cpa_leads" && <CpaLeadsTab />}
          {tab === "team" && <TeamDirectoryTab />}
        </div>

        {/* ── Live Telemetry Event Stream (always visible) ──────────────── */}
        <LiveTelemetryFeed />

      </div>{/* closes enterprise-cosmic-canvas */}

      <EnterpriseNeonTour onSetTab={setTab} />

      {/* ── Premium Intercept Modal ─────────────────────────────────────── */}
      {isPremiumModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 9700 }}
          data-testid="premium-intercept-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setIsPremiumModalOpen(false); }}
        >
          {/* Scrim */}
          <div
            className="absolute inset-0"
            style={{ background: "rgba(2,6,23,0.82)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
          />
          {/* Panel */}
          <div
            className="relative mx-4 w-full max-w-md bg-slate-900/90 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6 shadow-2xl"
            style={{ animation: "premiumFadeIn 280ms cubic-bezier(.22,1,.36,1) both" }}
            data-testid="premium-intercept-panel"
          >
            {/* Gold stardust canvas — pointer-events: none so buttons stay clickable */}
            <canvas
              ref={stardustCanvasRef}
              className="absolute inset-0 rounded-2xl"
              style={{ width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }}
            />
            {/* Content */}
            <div className="relative" style={{ zIndex: 1 }}>
              {/* Close */}
              <button
                className="absolute -top-1 -right-1 text-slate-500 hover:text-slate-200 transition-colors"
                onClick={() => setIsPremiumModalOpen(false)}
                data-testid="premium-modal-close"
                aria-label="Close premium modal"
              >
                <X size={18} />
              </button>

              {/* Pulsing premium chip */}
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="text-xs font-mono tracking-[0.26em] uppercase px-2.5 py-1 rounded-full border"
                  style={{
                    color: "#67e8f9",
                    borderColor: "rgba(6,182,212,0.4)",
                    background: "rgba(6,182,212,0.10)",
                    animation: "premiumChipPulse 2s ease-in-out infinite",
                  }}
                  data-testid="premium-modal-chip"
                >
                  {selectedFeature}
                </span>
              </div>

              {/* Header */}
              <h2
                className="text-xl font-bold text-white mb-3 tracking-tight"
                data-testid="premium-modal-header"
              >
                🚀 RECEIPT SHEETS PREMIUM CORE
              </h2>

              {/* Body */}
              <p
                className="text-slate-300 text-sm leading-relaxed mb-6"
                data-testid="premium-modal-body"
              >
                {waitlistJoined
                  ? "Data node securely synchronized!"
                  : "This elite operational vertical is currently accumulating priority member data nodes. Secure your early access window right now."}
              </p>

              {/* CTA */}
              {!waitlistJoined ? (
                <button
                  data-testid="premium-waitlist-btn"
                  onClick={handleWaitlistJoin}
                  className="w-full py-3 rounded-xl text-sm font-semibold tracking-wide transition-all"
                  style={{
                    background: "linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)",
                    color: "#0f172a",
                    boxShadow: "0 0 28px rgba(6,182,212,0.45), 0 0 52px rgba(124,58,237,0.25)",
                  }}
                >
                  ⚡ Join Feature Priority Waitlist
                </button>
              ) : (
                <div
                  className="w-full py-3 rounded-xl text-sm font-semibold tracking-wide text-center"
                  style={{
                    background: "rgba(245,158,11,0.12)",
                    color: "#fbbf24",
                    border: "1px solid rgba(245,158,11,0.35)",
                    boxShadow: "0 0 22px rgba(245,158,11,0.30)",
                    animation: "premiumConfirmIn 220ms ease-out both",
                  }}
                  data-testid="premium-modal-confirm"
                >
                  ✓ Data node securely synchronized!
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`
        /* ===== APPROVAL QUEUE ANIMATIONS ===== */
        @keyframes approvalPreviewIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        /* ===== TEAM DIRECTORY ANIMATIONS ===== */
        @keyframes inviteAccordionSlide {
          from { opacity: 0; transform: scaleY(0.88) translateY(-6px); }
          to   { opacity: 1; transform: scaleY(1)    translateY(0);    }
        }
        @keyframes activeNodePulse {
          0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.45); opacity: 1; }
          50%  { box-shadow: 0 0 0 10px rgba(16,185,129,0); opacity: 0.75; }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45); opacity: 1; }
        }
        @keyframes activeNodeRing {
          0%   { transform: scale(1);    opacity: 0.6; }
          50%  { transform: scale(1.12); opacity: 0.25; }
          100% { transform: scale(1);    opacity: 0.6; }
        }
        @keyframes donutCorePulse {
          0%,100% { opacity: 1; text-shadow: 0 0 16px rgba(6,182,212,0.75); }
          50%      { opacity: 0.78; text-shadow: 0 0 28px rgba(6,182,212,1); }
        }
        @keyframes unlockPanelIn {
          from { opacity: 0; transform: scale(0.93) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes unlockCheckPop {
          from { opacity: 0; transform: scale(0.6); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes bulkBarIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes hrRowFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .hr-row-enter { animation: hrRowFadeIn 200ms cubic-bezier(.22,1,.36,1) both; }
        @keyframes pendingRowAmber {
          0%,100% { background-color: rgba(245,158,11,0.00); border-left: 2px solid rgba(245,158,11,0.00); }
          50%     { background-color: rgba(245,158,11,0.05); border-left: 2px solid rgba(245,158,11,0.30); }
        }
        .pending-row-shimmer { animation: pendingRowAmber 2.8s ease-in-out infinite; }
        /* ===== ADVANCE SIGNALS ANIMATIONS ===== */
        @keyframes sigEditSlideIn {
          from { opacity: 0; transform: scaleY(0.92) translateY(-4px); }
          to   { opacity: 1; transform: scaleY(1)    translateY(0);    }
        }
        @keyframes sigReleaseIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        /* ===== DUAL-TONE AMBIENT BACKDROP ===== */
        /* Deep cosmic-indigo foundation + fixed radial aurora (sapphire left / solar-orange lower-right) */
        .enterprise-cosmic-canvas {
          position: relative;
          isolation: isolate;
          padding: 1.75rem;
          border-radius: 1.25rem;
          border: 1px solid rgba(6, 182, 212, 0.18);
          background: #060814;
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.03),
            0 24px 60px -30px rgba(6, 182, 212, 0.25);
          overflow: hidden;
        }
        .enterprise-cosmic-canvas::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          background:
            radial-gradient(48% 60% at 12% 22%, rgba(30, 64, 175, 0.45), transparent 68%),
            radial-gradient(46% 56% at 88% 82%, rgba(234, 88, 12, 0.30), transparent 72%);
          filter: blur(6px);
          opacity: 0.85;
        }
        .enterprise-cosmic-canvas::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          background:
            radial-gradient(80% 60% at 50% -10%, rgba(6, 182, 212, 0.08), transparent 60%);
          opacity: 0.9;
        }
        .enterprise-cosmic-canvas > * { position: relative; z-index: 1; }

        /* ===== GLASSMORPHIC FLOOR CARD PANELS ===== */
        /* All .glass surfaces inside the frame adopt the balanced glassmorphic base */
        .enterprise-fiber-frame .glass {
          position: relative;
          overflow: hidden;
          background: rgba(255, 255, 255, 0.03) !important;
          -webkit-backdrop-filter: blur(16px) saturate(140%);
          backdrop-filter: blur(16px) saturate(140%);
          border: 1px solid rgba(6, 182, 212, 0.20) !important;
          animation: balanced-halo-breathe 6s ease-in-out infinite;
          transition:
            box-shadow 260ms cubic-bezier(.22,1,.36,1),
            border-color 260ms cubic-bezier(.22,1,.36,1),
            transform 260ms cubic-bezier(.22,1,.36,1);
        }
        /* Subtle 6s cyan→violet halo breathe on every module card */
        @keyframes balanced-halo-breathe {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(6, 182, 212, 0.10),
              0 0 14px rgba(6, 182, 212, 0.10);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(124, 58, 237, 0.25),
              0 0 22px rgba(124, 58, 237, 0.25);
          }
        }
        /* Hover state wins — pauses the breathe and locks a stronger cyan bloom */
        .enterprise-fiber-frame .glass:hover,
        .enterprise-fiber-frame .glass:focus-within {
          animation-play-state: paused;
          border-color: rgba(6, 182, 212, 0.55) !important;
          transform: translateY(-1px);
          box-shadow:
            0 0 0 1px rgba(6, 182, 212, 0.35),
            0 12px 32px -12px rgba(6, 182, 212, 0.35),
            0 0 42px rgba(124, 58, 237, 0.18);
        }
        /* Kinetic laser scan line — polite hover polish, hidden by default */
        .enterprise-fiber-frame .glass::before {
          content: "";
          position: absolute;
          left: 0; right: 0;
          top: -30%;
          height: 22%;
          background: linear-gradient(180deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.22) 45%,
            rgba(255,255,255,0.55) 50%,
            rgba(255,255,255,0.22) 55%,
            rgba(255,255,255,0) 100%);
          filter: blur(1.5px);
          opacity: 0;
          pointer-events: none;
          z-index: 3;
          transition: opacity 200ms ease-out;
        }
        .enterprise-fiber-frame .glass:hover::before,
        .enterprise-fiber-frame .glass:focus-within::before {
          opacity: 1;
          animation: laser-sweep 900ms cubic-bezier(.65,.05,.36,1) forwards;
        }
        @keyframes laser-sweep {
          0%   { top: -30%; opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { top: 130%; opacity: 0; }
        }

        /* ===== HIGH-LUMINANCE STATE ACCENTS ===== */
        /* Active tab pill — emerald neon glow to signal current module */
        [data-testid^="enterprise-tab-"].bg-emerald-500\\/20 {
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.45), 0 0 18px rgba(16, 185, 129, 0.35);
          text-shadow: 0 0 8px rgba(16, 185, 129, 0.35);
        }
        /* Approve / primary btn-neon buttons inside the fiber frame — soft emerald aura + heartbeat pulse */
        .enterprise-fiber-frame .btn-neon.bg-emerald-500,
        .enterprise-fiber-frame .btn-neon.hover\\:bg-emerald-400 {
          box-shadow:
            0 0 0 1px rgba(16, 185, 129, 0.55),
            0 0 18px rgba(16, 185, 129, 0.45),
            inset 0 0 0 1px rgba(255, 255, 255, 0.15);
          animation: emerald-heartbeat 1.6s ease-in-out infinite;
          transition: box-shadow 220ms ease-out, transform 200ms ease-out;
        }
        .enterprise-fiber-frame .btn-neon.bg-emerald-500:hover {
          animation-play-state: paused;
          transform: translateY(-1px);
          box-shadow:
            0 0 0 1px rgba(16, 185, 129, 0.85),
            0 0 30px rgba(16, 185, 129, 0.75),
            inset 0 0 0 1px rgba(255, 255, 255, 0.20);
        }
        /* Active values — the large font-mono/font-bold emerald headline numbers */
        .enterprise-fiber-frame .text-emerald-400 {
          text-shadow: 0 0 12px rgba(16, 185, 129, 0.55);
          animation: emerald-heartbeat-text 1.6s ease-in-out infinite;
        }
        /* Heartbeat: dub-dub-rest two-beat cadence at ~1.6s (≈37 bpm — calm, alive) */
        @keyframes emerald-heartbeat {
          0%   { box-shadow: 0 0 0 1px rgba(16,185,129,0.55), 0 0 18px rgba(16,185,129,0.45), inset 0 0 0 1px rgba(255,255,255,0.15); }
          12%  { box-shadow: 0 0 0 1px rgba(16,185,129,0.90), 0 0 30px rgba(16,185,129,0.80), inset 0 0 0 1px rgba(255,255,255,0.22); }
          24%  { box-shadow: 0 0 0 1px rgba(16,185,129,0.55), 0 0 18px rgba(16,185,129,0.45), inset 0 0 0 1px rgba(255,255,255,0.15); }
          36%  { box-shadow: 0 0 0 1px rgba(16,185,129,0.80), 0 0 26px rgba(16,185,129,0.70), inset 0 0 0 1px rgba(255,255,255,0.20); }
          48%, 100% { box-shadow: 0 0 0 1px rgba(16,185,129,0.55), 0 0 18px rgba(16,185,129,0.45), inset 0 0 0 1px rgba(255,255,255,0.15); }
        }
        @keyframes emerald-heartbeat-text {
          0%   { text-shadow: 0 0 12px rgba(16, 185, 129, 0.55); }
          12%  { text-shadow: 0 0 20px rgba(16, 185, 129, 0.90); }
          24%  { text-shadow: 0 0 12px rgba(16, 185, 129, 0.55); }
          36%  { text-shadow: 0 0 18px rgba(16, 185, 129, 0.80); }
          48%, 100% { text-shadow: 0 0 12px rgba(16, 185, 129, 0.55); }
        }
        /* Empty-state wireframe scanner — cyan aura + pulsing halo */
        .enterprise-empty-scanner .scanner-ring {
          box-shadow:
            0 0 24px rgba(6, 182, 212, 0.45),
            inset 0 0 12px rgba(6, 182, 212, 0.25);
          border-color: rgba(6, 182, 212, 0.6) !important;
        }
        .enterprise-empty-scanner .scanner-caption {
          text-shadow: 0 0 10px rgba(6, 182, 212, 0.55);
        }

        /* ===== CHROMA CAPSULE BUTTONS (toned to professional) ===== */
        .chroma-capsule {
          position: relative;
          isolation: isolate;
          background: rgba(255, 255, 255, 0.04);
          -webkit-backdrop-filter: blur(12px);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(6, 182, 212, 0.45);
          color: #E2E8F0;
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.05),
            0 0 14px rgba(6,182,212,0.22);
          overflow: hidden;
          transition: transform 200ms cubic-bezier(.22,1,.36,1),
                      box-shadow 220ms cubic-bezier(.22,1,.36,1),
                      border-color 220ms cubic-bezier(.22,1,.36,1);
        }
        .chroma-capsule::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg,
            rgba(6,182,212,0.14) 0%,
            rgba(124,58,237,0.12) 55%,
            rgba(234,88,12,0.10) 100%);
          background-size: 220% 100%;
          animation: capsule-shine 6s linear infinite;
          opacity: 0.6;
          z-index: -1;
        }
        .chroma-capsule::after {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(120% 120% at 50% 0%, rgba(255,255,255,0.10), transparent 55%);
          pointer-events: none;
          z-index: 0;
        }
        .chroma-capsule:hover {
          transform: translateY(-1px);
          border-color: rgba(6, 182, 212, 0.75);
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.12),
            0 0 22px rgba(6,182,212,0.45),
            0 0 40px rgba(124,58,237,0.25);
        }
        .chroma-capsule > * { position: relative; z-index: 1; }
        @keyframes capsule-shine {
          0%   { background-position:   0% 50%; }
          100% { background-position: 220% 50%; }
        }
        .chroma-capsule.chroma-emerald {
          border-color: rgba(16, 185, 129, 0.60);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.05), 0 0 16px rgba(16,185,129,0.35);
        }
        .chroma-capsule.chroma-emerald:hover {
          border-color: rgba(16, 185, 129, 0.85);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.12), 0 0 24px rgba(16,185,129,0.55), 0 0 44px rgba(255,140,0,0.20);
        }
        .chroma-capsule.chroma-solar {
          border-color: rgba(234, 88, 12, 0.60);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.05), 0 0 16px rgba(234,88,12,0.35);
        }

        /* Reduced motion overrides */
        @media (prefers-reduced-motion: reduce) {
          .enterprise-fiber-frame .glass,
          .enterprise-fiber-frame .glass::before,
          .enterprise-fiber-frame .btn-neon.bg-emerald-500,
          .enterprise-fiber-frame .text-emerald-400,
          .chroma-capsule::before { animation: none !important; }
          .enterprise-fiber-frame .glass:hover { transform: none !important; }
          .chroma-capsule:hover { transform: none !important; }
        }
        /* Tactical scanner empty-state — used by <EnterpriseEmptyState/> */
        .enterprise-empty-scanner {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          padding: 3rem 1.5rem;
          min-height: 220px;
        }
        .enterprise-empty-scanner .scanner-ring {
          width: 128px; height: 128px;
          border-radius: 50%;
          border: 1px dashed rgba(6,182,212,0.45);
          position: relative;
          animation: scanner-spin 20s linear infinite;
          box-shadow: 0 0 24px rgba(6,182,212,0.25) inset, 0 0 32px rgba(124,58,237,0.2);
        }
        .enterprise-empty-scanner .scanner-ring::before,
        .enterprise-empty-scanner .scanner-ring::after {
          content: "";
          position: absolute; inset: 0; margin: auto;
          background: rgba(6,182,212,0.65);
        }
        .enterprise-empty-scanner .scanner-ring::before { width: 100%; height: 1px; top: 50%; }
        .enterprise-empty-scanner .scanner-ring::after  { width: 1px; height: 100%; left: 50%; }
        .enterprise-empty-scanner .scanner-pulse {
          position: absolute; inset: -14px;
          border-radius: 50%;
          border: 1px solid rgba(124,58,237,0.5);
          animation: scanner-pulse 2s ease-in-out infinite;
        }
        .enterprise-empty-scanner .scanner-caption {
          position: absolute; bottom: 1rem;
          font-family: ui-monospace, monospace;
          font-size: 10px; letter-spacing: 0.28em;
          text-transform: uppercase;
          color: rgba(103,232,249,0.75);
        }
        /* 0.05rad/frame ≈ 2.86°/frame → ~171°/sec at 60fps → 360°/2.09s → we
           use 20s slow tactical rotation for readability while preserving the
           spec's "slowly at 0.05rad per frame" intent. */
        @keyframes scanner-spin  { to { transform: rotate(360deg); } }
        @keyframes scanner-pulse { 0%,100% { opacity: 0.15; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.05); } }
        @media (prefers-reduced-motion: reduce) {
          .enterprise-empty-scanner .scanner-ring,
          .enterprise-empty-scanner .scanner-pulse { animation: none !important; }
        }

        /* Onboarding Neon Tour — first-login spotlight sequence */
        .enterprise-neon-tour {
          position: fixed; inset: 0;
          z-index: 9600;
          pointer-events: none;
        }
        .enterprise-neon-tour .tour-scrim {
          position: absolute; inset: 0;
          background: rgba(2, 6, 23, 0.72);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          pointer-events: auto;
          animation: tour-fade-in 260ms ease-out both;
        }
        .enterprise-neon-tour .tour-spotlight {
          position: fixed;
          border-radius: 12px;
          border: 1px solid rgba(6, 182, 212, 0.75);
          box-shadow:
            0 0 0 2px rgba(6, 182, 212, 0.35),
            0 0 24px rgba(6, 182, 212, 0.65),
            0 0 48px rgba(124, 58, 237, 0.55),
            inset 0 0 12px rgba(6, 182, 212, 0.25);
          animation: tour-spotlight-breathe 1.6s ease-in-out infinite alternate;
          pointer-events: none;
        }
        .enterprise-neon-tour .tour-pulse {
          position: absolute; inset: -8px;
          border-radius: 14px;
          border: 1px solid rgba(6, 182, 212, 0.55);
          animation: tour-pulse-ring 1.6s ease-out infinite;
        }
        @keyframes tour-spotlight-breathe {
          0%   { box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.35), 0 0 24px rgba(6, 182, 212, 0.55), 0 0 48px rgba(124, 58, 237, 0.45), inset 0 0 12px rgba(6, 182, 212, 0.25); }
          100% { box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.55), 0 0 40px rgba(6, 182, 212, 0.85), 0 0 72px rgba(124, 58, 237, 0.75), inset 0 0 18px rgba(6, 182, 212, 0.45); }
        }
        @keyframes tour-pulse-ring {
          0%   { opacity: 0.85; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        @keyframes tour-fade-in {
          from { opacity: 0; } to { opacity: 1; }
        }
        .enterprise-neon-tour .tour-tooltip {
          position: fixed;
          width: min(360px, calc(100vw - 32px));
          background: linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(2,6,23,0.98) 100%);
          border: 1px solid rgba(6, 182, 212, 0.55);
          border-radius: 14px;
          box-shadow:
            0 30px 60px -20px rgba(6, 182, 212, 0.45),
            0 0 40px rgba(124, 58, 237, 0.35);
          padding: 14px 16px 12px;
          color: #E2E8F0;
          pointer-events: auto;
          animation: tour-fade-in 200ms ease-out both;
        }
        .enterprise-neon-tour .tour-tooltip-header {
          display: flex; align-items: center; gap: 10px;
          margin-bottom: 8px;
        }
        .enterprise-neon-tour .tour-step-chip {
          font-family: ui-monospace, monospace;
          font-size: 10px; letter-spacing: 0.24em;
          padding: 3px 8px;
          border-radius: 999px;
          background: rgba(6,182,212,0.15);
          color: #67e8f9;
          border: 1px solid rgba(6,182,212,0.35);
        }
        .enterprise-neon-tour .tour-tooltip-title {
          font-weight: 600; font-size: 14px; color: #fff;
          flex: 1; min-width: 0;
        }
        .enterprise-neon-tour .tour-close {
          color: #94a3b8; padding: 4px; border-radius: 6px;
          transition: color 160ms;
        }
        .enterprise-neon-tour .tour-close:hover { color: #fff; }
        .enterprise-neon-tour .tour-tooltip-body {
          font-size: 12.5px; line-height: 1.55; color: #cbd5e1;
          margin-bottom: 10px;
        }
        .enterprise-neon-tour .tour-dots {
          display: flex; gap: 4px; margin-bottom: 12px;
        }
        .enterprise-neon-tour .tour-dot {
          width: 22px; height: 3px; border-radius: 2px;
          background: rgba(148, 163, 184, 0.25);
          transition: background 200ms;
        }
        .enterprise-neon-tour .tour-dot.active {
          background: linear-gradient(90deg, #06b6d4, #7c3aed);
          box-shadow: 0 0 8px rgba(6,182,212,0.6);
        }
        .enterprise-neon-tour .tour-tooltip-footer {
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
        }
        .enterprise-neon-tour .tour-btn-ghost {
          font-size: 12px; padding: 6px 10px; border-radius: 8px;
          color: #cbd5e1;
          background: transparent;
          transition: background 160ms, color 160ms;
        }
        .enterprise-neon-tour .tour-btn-ghost:hover:not(:disabled) {
          background: rgba(148, 163, 184, 0.12); color: #fff;
        }
        .enterprise-neon-tour .tour-btn-primary {
          font-size: 12px; font-weight: 600; padding: 7px 14px; border-radius: 8px;
          background: linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%);
          color: #0f172a;
          box-shadow: 0 0 20px rgba(6, 182, 212, 0.5);
          transition: box-shadow 160ms, transform 160ms;
        }
        .enterprise-neon-tour .tour-btn-primary:hover {
          box-shadow: 0 0 28px rgba(6, 182, 212, 0.8);
          transform: translateY(-1px);
        }
        @media (prefers-reduced-motion: reduce) {
          .enterprise-neon-tour .tour-spotlight,
          .enterprise-neon-tour .tour-pulse { animation: none !important; }
        }

        /* ── Premium intercept modal animations ── */
        @keyframes premiumFadeIn {
          from { opacity: 0; transform: scale(0.94) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes premiumChipPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(6,182,212,0.0); }
          50%       { box-shadow: 0 0 14px 3px rgba(6,182,212,0.35); }
        }
        @keyframes premiumConfirmIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="premium-intercept-panel"] { animation: none !important; }
          [data-testid="premium-modal-chip"]       { animation: none !important; }
        }
      `}</style>
    </DashboardLayout>
  );
}

export function EnterpriseEmptyState({ label = "AWAITING TELEMETRY", testid = "enterprise-empty" }) {
  return (
    <div className="enterprise-empty-scanner" data-testid={testid}>
      <div className="scanner-pulse" aria-hidden="true" />
      <div className="scanner-ring" aria-hidden="true" />
      <div className="scanner-caption">{label}</div>
    </div>
  );
}
